import { motion } from "framer-motion";
import { useEffect, useRef, useState } from "react";
import { ArrowUpRight, ArrowRight, Check, Search, Star, AlertTriangle, Package, DollarSign, Sparkles, X as XIcon } from "lucide-react";
import { TYPEFORM, LOGO_SRC , withUTM } from "../data/landing";
const T = Object.fromEntries(Object.entries(TYPEFORM).map(([k, v]) => [k, withUTM(v, "for-licensees")]));

const A = {
  dashboardVideo: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/710upey8_List.mp4",
  paulScardino: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/d2c1pyb0_paul-scardino-funko-pop-chi-e.jpeg",
  maximizeProfits: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/m1zrxbmf_higher%20rez.png",
  cannotShip: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/dzwk3apu_image.png",
  geoShield: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/0zah2x2y_image.png",
  comicCon: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/t38raqgd_Screenshot%202026-05-02%20124238.png",
  premiereLogo: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/gs9xwyp4_Premimere%20Drop%20logo.png",
  premiereLeaderboard: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/m7ixlnt4_image.png",
  einsightsDisney: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/n8onuyxg_image.png",
  eLicenseeLogo: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/7wgzks5n_eLicensee.png",
  verifiedSeal: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/laofq197_eGeez%20verified%20-%20gold-%20circle.png",
  step1Dashboard: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/acc5abc0_Dashboard22.png",
  step3Products: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/pjqrv7al_image.png",
  eShipLogo: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/sjqgkjp5_Ship.png",
  eShipAppScreen: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/tqciwod0_egeez-screen.png",
  bgBlue: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/bvyosrzq_BG1.png",
  bgPink: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/raqsjlz5_BG3.png",
  bgOrange: "https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/pphq1t1h_BG4.png",
};

const fadeUp = {
  initial: { opacity: 0, y: 24 },
  whileInView: { opacity: 1, y: 0 },
  viewport: { once: true, margin: "-60px" },
  transition: { duration: 0.6, ease: [0.16, 1, 0.3, 1] },
};

function Eyebrow({ children, color = "#0041E6" }) {
  return (
    <div className="inline-flex items-center gap-2 text-[11px] md:text-[12px] font-bold tracking-[0.28em] uppercase" style={{ color }}>
      <span className="inline-block w-6 h-px" style={{ background: color }} />
      {children}
    </div>
  );
}

// Premium laptop frame showcasing the SaaS dashboard video
// - Autoplays on load, silent, loops faster
// - Subtle zoom pulse to feel "alive"
// - "Ready to Sell" status pops at loop end
// - Hover overlay: "Stop Hovering. Start Selling."
function LaptopVideo({ src }) {
  const videoRef = useRef(null);
  const [readyPulse, setReadyPulse] = useState(false);
  const [hovered, setHovered] = useState(false);

  useEffect(() => {
    const v = videoRef.current;
    if (!v) return;
    v.playbackRate = 2.5; // faster — feels like quick demo
    const p = v.play();
    if (p && typeof p.catch === "function") p.catch(() => {});

    const onTimeUpdate = () => {
      if (!v.duration) return;
      // show "Ready to Sell" in the last ~0.6s of each loop
      setReadyPulse(v.currentTime >= v.duration - 0.6);
    };
    const onLoaded = () => {
      v.playbackRate = 2.5;
    };
    v.addEventListener("timeupdate", onTimeUpdate);
    v.addEventListener("loadedmetadata", onLoaded);
    return () => {
      v.removeEventListener("timeupdate", onTimeUpdate);
      v.removeEventListener("loadedmetadata", onLoaded);
    };
  }, []);

  return (
    <motion.div
      animate={{ y: [0, -10, 0] }}
      transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      className="relative w-full max-w-[640px] mx-auto group"
      data-testid="licensees-dashboard-video"
    >
      {/* Soft glow under laptop */}
      <div className="absolute -inset-10 bg-[#0041E6]/25 blur-[80px] rounded-full pointer-events-none" aria-hidden />

      {/* Laptop body */}
      <div className="relative bg-[#1A1A1F] rounded-t-[18px] p-3 pb-4 shadow-[0_40px_80px_-20px_rgba(0,0,0,0.5)]">
        {/* Camera notch */}
        <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#3a3a40] border border-black/30" />
        {/* Browser-style chrome bar */}
        <div className="flex items-center gap-1.5 px-2 pb-2 pt-1">
          <span className="w-2 h-2 rounded-full bg-[#FF5F57]" />
          <span className="w-2 h-2 rounded-full bg-[#FEBC2E]" />
          <span className="w-2 h-2 rounded-full bg-[#28C840]" />
          <div className="ml-3 flex-1 h-[18px] rounded-md bg-[#2A2A30] flex items-center px-2 text-[9px] text-white/50 font-mono tracking-wide">
            elicensee.egeez.com
          </div>
        </div>
        {/* Screen */}
        <div className="relative rounded-[6px] overflow-hidden bg-white aspect-[16/10]">
          <motion.video
            ref={videoRef}
            src={src}
            muted
            loop
            autoPlay
            playsInline
            preload="auto"
            animate={{ scale: [1, 1.06, 1] }}
            transition={{ duration: 7, repeat: Infinity, ease: "easeInOut" }}
            className="w-full h-full object-contain bg-white"
          />

          {/* Ready-to-Sell status tooltip (shows at end of each loop) */}
          <motion.div
            initial={false}
            animate={{
              opacity: readyPulse ? 1 : 0,
              y: readyPulse ? 0 : 8,
              scale: readyPulse ? 1 : 0.96,
            }}
            transition={{ duration: 0.25, ease: "easeOut" }}
            className="absolute top-3 left-3 pointer-events-none"
          >
            <div className="flex items-center gap-1.5 bg-[#0041E6] text-white text-[10px] font-bold uppercase tracking-[0.18em] px-2.5 py-1.5 rounded-full shadow-xl">
              <span className="w-1.5 h-1.5 rounded-full bg-[#FFD400] animate-pulse" />
              Ready to Sell
            </div>
          </motion.div>

          {/* Hover overlay */}
          <motion.div
            initial={false}
            animate={{ opacity: hovered ? 1 : 0 }}
            transition={{ duration: 0.25 }}
            className="absolute inset-0 flex items-center justify-center pointer-events-none"
          >
            <div className="absolute inset-0 bg-gradient-to-br from-[#0041E6]/80 via-[#0041E6]/60 to-[#001F7A]/80 backdrop-blur-[2px]" />
            <motion.div
              animate={{ y: hovered ? 0 : 8, opacity: hovered ? 1 : 0 }}
              transition={{ duration: 0.3, ease: [0.16, 1, 0.3, 1] }}
              className="relative z-10 text-center px-6"
            >
              <div className="text-[11px] font-bold tracking-[0.3em] uppercase text-[#FFD400]">
                Hey you —
              </div>
              <div className="mt-2 font-display text-white leading-[1] text-[26px] md:text-[36px]">
                Stop Hovering.<br />
                <span className="italic text-[#FFD400]">Start Selling.</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
      {/* Laptop base / hinge */}
      <div className="relative h-[14px] bg-gradient-to-b from-[#2a2a30] via-[#1A1A1F] to-[#0a0a0d] rounded-b-[6px] mx-[-14px] shadow-md">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-24 h-1.5 bg-black/40 rounded-full" />
      </div>
      <div className="h-2 bg-gradient-to-b from-black/35 to-transparent mx-[-28px] rounded-b-[22px]" aria-hidden />

      {/* Live pill */}
      <div className="absolute top-5 right-5 z-10 flex items-center gap-1.5 bg-[#0041E6] text-white text-[10px] font-bold tracking-[0.2em] uppercase px-2.5 py-1 rounded-full shadow-lg">
        <span className="relative flex w-1.5 h-1.5">
          <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-[#FFD400] opacity-75" />
          <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-[#FFD400]" />
        </span>
        Live
      </div>
    </motion.div>
  );
}

// Step visuals — each step renders a custom interactive panel on the left
function StepVisual({ index, accent, step1Img, step3Img }) {
  if (index === 0) {
    // STEP 1 — laptop with dashboard image
    return (
      <motion.div
        animate={{ y: [0, -8, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="relative w-full max-w-[520px]"
      >
        <div className="absolute -inset-8 bg-[#0041E6]/15 blur-[60px] rounded-full pointer-events-none" aria-hidden />
        <div className="relative bg-[#1A1A1F] rounded-t-[16px] p-2.5 pb-3 shadow-[0_30px_60px_-15px_rgba(0,0,0,0.4)]">
          <div className="absolute top-1.5 left-1/2 -translate-x-1/2 w-2 h-2 rounded-full bg-[#3a3a40] border border-black/30" />
          <div className="flex items-center gap-1.5 px-2 pb-1.5 pt-0.5">
            <span className="w-2 h-2 rounded-full bg-[#FF5F57]" />
            <span className="w-2 h-2 rounded-full bg-[#FEBC2E]" />
            <span className="w-2 h-2 rounded-full bg-[#28C840]" />
            <div className="ml-3 flex-1 h-[16px] rounded-md bg-[#2A2A30] flex items-center px-2 text-[8px] text-white/50 font-mono tracking-wide">
              elicensee.egeez.com/dashboard
            </div>
          </div>
          <div className="rounded-[5px] overflow-hidden bg-white">
            <img src={step1Img} alt="Step 1 — eLicensee dashboard" className="w-full h-auto" draggable={false} />
          </div>
        </div>
        <div className="relative h-[12px] bg-gradient-to-b from-[#2a2a30] via-[#1A1A1F] to-[#0a0a0d] rounded-b-[6px] mx-[-12px] shadow-md">
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-20 h-1 bg-black/40 rounded-full" />
        </div>
        {/* ePIN badge */}
        <motion.div
          initial={{ opacity: 0, scale: 0.7, y: 10 }}
          whileInView={{ opacity: 1, scale: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, delay: 0.4 }}
          className="absolute -top-3 -right-3 z-10"
        >
          <motion.div
            animate={{ rotate: [-3, 3, -3] }}
            transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
            className="bg-[#0041E6] text-white text-[10px] font-black tracking-[0.2em] uppercase px-3 py-1.5 rounded-full shadow-xl flex items-center gap-1.5"
          >
            <span className="w-1.5 h-1.5 rounded-full bg-[#FFD400] animate-pulse" />
            ePIN · Issued
          </motion.div>
        </motion.div>
      </motion.div>
    );
  }

  if (index === 1) {
    // STEP 2 — animated green progress bar
    const stages = ["Agree", "Profile", "My Licensors", "Payout", "Ready to Sell"];
    return (
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="relative w-full max-w-[520px]"
      >
        <div className="absolute -inset-8 bg-[#22C55E]/12 blur-[60px] rounded-full pointer-events-none" aria-hidden />

        <div className="relative bg-white rounded-2xl shadow-[0_30px_60px_-15px_rgba(0,0,0,0.18)] ring-1 ring-black/5 p-6 md:p-8">
          <div className="flex items-center justify-between mb-5">
            <div className="flex items-center gap-2">
              <span className="text-[11px] font-black tracking-[0.22em] uppercase text-black/70">
                Onboarding Progress
              </span>
            </div>
            <span className="text-[11px] font-black bg-[#E8FCEF] text-[#15803D] px-2.5 py-1 rounded-full">
              80%
            </span>
          </div>

          {/* Progress track */}
          <div className="relative">
            <div className="absolute left-3 right-3 top-[14px] h-[3px] bg-black/8 rounded-full" aria-hidden />
            <motion.div
              className="absolute left-3 top-[14px] h-[3px] rounded-full"
              style={{ background: "linear-gradient(90deg, #22C55E 0%, #16A34A 100%)" }}
              initial={{ width: 0 }}
              whileInView={{ width: "calc(75% - 12px)" }}
              viewport={{ once: true }}
              transition={{ duration: 2.4, ease: [0.16, 1, 0.3, 1], delay: 0.3 }}
            />

            <div className="grid grid-cols-5 gap-1 relative">
              {stages.map((s, i) => (
                <motion.div
                  key={i}
                  initial={{ opacity: 0, scale: 0.8 }}
                  whileInView={{ opacity: 1, scale: 1 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: 0.5 + i * 0.4 }}
                  className="flex flex-col items-center gap-2"
                >
                  <motion.div
                    initial={{ background: "#E5E7EB" }}
                    whileInView={{
                      background: i <= 3 ? "#22C55E" : "#E5E7EB",
                    }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.3, delay: 0.5 + i * 0.4 }}
                    className="relative w-7 h-7 rounded-full flex items-center justify-center text-white shadow-md"
                  >
                    {i <= 3 ? (
                      <Check size={14} strokeWidth={3} />
                    ) : (
                      <motion.span
                        animate={{ scale: [1, 1.2, 1] }}
                        transition={{ duration: 1.2, repeat: Infinity }}
                        className="w-2 h-2 rounded-full bg-[#0041E6]"
                      />
                    )}
                    {i === 3 && (
                      <motion.div
                        className="absolute inset-0 rounded-full border-2 border-[#22C55E]"
                        animate={{ scale: [1, 1.6, 1.6], opacity: [0.6, 0, 0] }}
                        transition={{ duration: 1.6, repeat: Infinity, ease: "easeOut" }}
                      />
                    )}
                  </motion.div>
                  <span
                    className={`text-[9px] md:text-[10px] font-bold text-center leading-tight ${
                      i <= 3 ? "text-[#15803D]" : "text-black/50"
                    }`}
                  >
                    {s}
                  </span>
                </motion.div>
              ))}
            </div>
          </div>

          {/* Bottom CTA */}
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5, delay: 2.4 }}
            className="mt-6 flex items-center justify-between bg-[#F0FDF4] border border-[#86EFAC] rounded-xl px-3 py-2.5"
          >
            <div className="text-[12px] font-bold text-[#15803D]">
              Almost there — 1 step to launch
            </div>
            <span className="text-[10px] font-black text-[#15803D] bg-white px-2 py-1 rounded-full">2 min</span>
          </motion.div>
        </div>
      </motion.div>
    );
  }

  // STEP 3 — products image with green Ready to Sell alert + powered by eSmart AI
  return (
    <motion.div
      animate={{ y: [0, -8, 0] }}
      transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
      className="relative w-full max-w-[520px]"
    >
      <div className="absolute -inset-8 bg-[#0041E6]/15 blur-[60px] rounded-full pointer-events-none" aria-hidden />

      <div className="relative rounded-2xl overflow-hidden shadow-[0_30px_60px_-15px_rgba(0,0,0,0.25)] ring-1 ring-black/5 bg-white">
        <img src={step3Img} alt="Step 3 — Product List with Claim" className="w-full h-auto block" draggable={false} />
      </div>

      {/* Animated green Ready to Sell alert */}
      <motion.div
        initial={{ opacity: 0, scale: 0.85, y: -8 }}
        whileInView={{ opacity: 1, scale: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.3 }}
        className="absolute -top-4 -right-3 z-20"
      >
        <motion.div
          animate={{ y: [0, -4, 0] }}
          transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
          className="relative bg-[#22C55E] text-white px-3.5 py-2 rounded-2xl shadow-[0_18px_30px_-10px_rgba(34,197,94,0.55)] flex items-center gap-2"
        >
          <span className="relative flex w-2 h-2">
            <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-white opacity-75" />
            <span className="relative inline-flex rounded-full h-2 w-2 bg-white" />
          </span>
          <div>
            <div className="text-[10px] font-black tracking-[0.18em] uppercase">Ready to Sell</div>
            <div className="text-[9px] opacity-85 font-bold">3 SKUs activated</div>
          </div>
        </motion.div>
      </motion.div>

      {/* Powered by eSmart AI label */}
      <motion.div
        initial={{ opacity: 0, y: 8 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.5 }}
        className="mt-4 flex items-center justify-center gap-2"
      >
        <span className="text-[9px] font-bold tracking-[0.28em] uppercase text-black/40">Powered by</span>
        <div className="inline-flex items-center gap-1.5 bg-black text-white text-[10px] font-black tracking-[0.2em] uppercase px-3 py-1.5 rounded-full">
          <Sparkles size={10} className="text-[#FFD400]" fill="#FFD400" />
          eSmart AI
        </div>
      </motion.div>
    </motion.div>
  );
}

// Zoomed-in inventory low-stock alert card — minimal, animated
function InventoryAlertCard() {
  const items = [
    { name: "Disney × Adidas Sneakers", sku: "SKU 884210", qty: 12, status: "ok" },
    { name: "Marvel Ironman Helmet", sku: "SKU 552038", qty: 3, status: "low" },
    { name: "Warner Batmobile 1:18", sku: "SKU 710984", qty: 28, status: "ok" },
  ];
  return (
    <div className="relative w-full max-w-[380px] mx-auto">
      {/* Floating laptop-ish card */}
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="relative rounded-2xl bg-white shadow-[0_30px_60px_-15px_rgba(0,0,0,0.35)] p-4 ring-1 ring-black/5"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-black/80">
            <Package size={14} />
            <span className="text-[11px] font-bold tracking-wider uppercase">Inventory</span>
          </div>
          <span className="text-[9px] font-mono text-black/40">LIVE</span>
        </div>

        <div className="space-y-2">
          {items.map((it, i) => {
            const isLow = it.status === "low";
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, x: -6 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.4, delay: i * 0.1 }}
                className={`flex items-center gap-3 rounded-xl p-2.5 border transition-all ${
                  isLow
                    ? "bg-[#FFF4F4] border-[#FFB4B4] hover:shadow-md"
                    : "bg-[#F8F8FB] border-black/5"
                }`}
              >
                <div className={`w-8 h-8 rounded-lg flex-shrink-0 ${isLow ? "bg-[#FFDADA]" : "bg-black/5"}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-[11px] font-bold text-black truncate">{it.name}</div>
                  <div className="text-[9px] text-black/50 font-mono">{it.sku}</div>
                </div>
                <div className="text-right">
                  <div className={`text-[13px] font-black leading-none ${isLow ? "text-[#E63946]" : "text-black"}`}>
                    {it.qty}
                  </div>
                  <div className="text-[8px] uppercase tracking-wider text-black/40 mt-0.5">qty</div>
                </div>
                {isLow && (
                  <motion.div
                    animate={{ scale: [1, 1.08, 1] }}
                    transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut" }}
                    className="flex items-center gap-1 bg-[#E63946] text-white text-[9px] font-bold uppercase tracking-wider px-2 py-1 rounded-full shadow-md"
                  >
                    <AlertTriangle size={10} strokeWidth={2.5} />
                    Low
                  </motion.div>
                )}
              </motion.div>
            );
          })}
        </div>

        {/* Footer banner alert that appears only if any low stock — emphasizes interaction */}
        <motion.div
          initial={{ opacity: 0, y: 10 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.5 }}
          className="mt-4 flex items-center gap-2 rounded-xl bg-[#0041E6] text-white px-3 py-2"
        >
          <AlertTriangle size={14} className="text-[#FFD400]" strokeWidth={2.5} />
          <div className="text-[11px] leading-tight">
            <span className="font-bold">1 SKU below 5 QTY</span>
            <span className="opacity-80"> — time to list on eGeez.</span>
          </div>
        </motion.div>
      </motion.div>

      {/* subtle shadow plate */}
      <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-[70%] h-3 bg-black/10 blur-xl rounded-full" aria-hidden />
    </div>
  );
}

// Payouts · My Licensors — animated card, minimal + live feel
function PayoutsCard() {
  const licensors = [
    { name: "DISNEY", color: "#0041E6", amount: 5112.69, delta: "+10%", month: "Nov" },
    { name: "WARNER", color: "#E63946", amount: 3840.00, delta: "+6%", month: "Nov" },
    { name: "MARVEL", color: "#D72638", amount: 2215.50, delta: "+14%", month: "Nov" },
  ];
  const total = licensors.reduce((a, b) => a + b.amount, 0);

  return (
    <div className="relative w-full max-w-[380px] mx-auto">
      {/* Floating cash emojis */}
      <motion.span
        aria-hidden
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: [0, 1, 1, 0], y: [10, -30, -60, -90] }}
        transition={{ duration: 3.2, repeat: Infinity, delay: 0.2, ease: "easeOut" }}
        className="absolute left-6 -top-4 text-2xl select-none pointer-events-none"
      >
        💵
      </motion.span>
      <motion.span
        aria-hidden
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: [0, 1, 1, 0], y: [10, -40, -80, -110] }}
        transition={{ duration: 3.6, repeat: Infinity, delay: 1.1, ease: "easeOut" }}
        className="absolute right-10 -top-2 text-xl select-none pointer-events-none"
      >
        💰
      </motion.span>
      <motion.span
        aria-hidden
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: [0, 1, 1, 0], y: [10, -25, -55, -85] }}
        transition={{ duration: 3, repeat: Infinity, delay: 2.2, ease: "easeOut" }}
        className="absolute left-1/2 -top-1 text-lg select-none pointer-events-none"
      >
        💵
      </motion.span>

      {/* Floating card */}
      <motion.div
        animate={{ y: [0, -6, 0] }}
        transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
        className="relative rounded-2xl bg-white shadow-[0_30px_60px_-15px_rgba(0,0,0,0.35)] p-4 ring-1 ring-black/5"
      >
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2 text-black/80">
            <DollarSign size={14} />
            <span className="text-[11px] font-bold tracking-wider uppercase">Payouts · My Licensors</span>
          </div>
          <span className="inline-flex items-center gap-1 text-[8px] font-bold tracking-[0.2em] uppercase bg-[#0041E6] text-white px-2 py-0.5 rounded-full">
            Licensee <ArrowRight size={8} strokeWidth={3} /> Fan
          </span>
        </div>

        {/* Total payout — animated count look */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="rounded-xl bg-[#0041E6] text-white p-3 mb-3 flex items-center justify-between"
        >
          <div>
            <div className="text-[9px] uppercase tracking-wider text-white/70">Total Payout · Nov</div>
            <div className="font-black text-[22px] text-[#FFD400] leading-none mt-1">
              $ {total.toLocaleString("en-US", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
            </div>
            <div className="text-[9px] text-white/70 mt-1">CAD · Direct deposit</div>
          </div>
          <motion.div
            animate={{ rotate: [0, -8, 8, 0] }}
            transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
            className="text-3xl"
          >
            💸
          </motion.div>
        </motion.div>

        <div className="space-y-2">
          {licensors.map((l, i) => (
            <motion.div
              key={l.name}
              initial={{ opacity: 0, x: -6 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.4, delay: i * 0.1 }}
              className="flex items-center gap-3 rounded-xl bg-[#F8F8FB] p-2.5 border border-black/5 hover:shadow-md transition-all"
            >
              <div
                className="w-8 h-8 rounded-lg flex items-center justify-center text-[9px] font-black text-white flex-shrink-0"
                style={{ background: l.color }}
              >
                {l.name.slice(0, 2)}
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[11px] font-bold text-black truncate">{l.name}</div>
                <div className="text-[9px] text-black/50">{l.month} · Total Earnings</div>
              </div>
              <div className="text-right">
                <div className="text-[13px] font-black text-black leading-none">
                  ${l.amount.toLocaleString("en-US", { minimumFractionDigits: 2 })}
                </div>
                <div className="text-[9px] text-green-600 font-bold mt-0.5">{l.delta}</div>
              </div>
            </motion.div>
          ))}
        </div>

        {/* Send Report button — mimics screenshot */}
        <motion.div
          initial={{ opacity: 0, y: 6 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.4 }}
          className="mt-3 flex items-center justify-between rounded-xl bg-gradient-to-r from-[#5B2EBE] to-[#3918A0] text-white px-3 py-2"
        >
          <div className="flex items-center gap-2">
            <span className="text-base">🧾</span>
            <span className="text-[11px] font-bold">Royalty report ready</span>
          </div>
          <button
            type="button"
            className="text-[10px] font-bold bg-white text-[#5B2EBE] rounded-full px-2.5 py-1 hover:bg-[#FFD400] transition-colors"
          >
            Send Report
          </button>
        </motion.div>
      </motion.div>

      <div className="absolute -bottom-3 left-1/2 -translate-x-1/2 w-[70%] h-3 bg-black/10 blur-xl rounded-full" aria-hidden />
    </div>
  );
}

// CSS mock of marketplace product grid phone
function MarketplacePhone() {
  return (
    <div className="relative w-[280px] md:w-[340px] aspect-[9/19] bg-black rounded-[36px] p-2 shadow-2xl">
      <div className="w-full h-full bg-white rounded-[28px] overflow-hidden flex flex-col">
        <div className="px-4 pt-4 pb-2">
          <div className="w-full h-9 rounded-full bg-[#F1F1F4] flex items-center px-4 gap-2 text-[10px] text-black/60">
            <Search size={12} /> Search · Anything under the sun
          </div>
          <div className="flex gap-2 mt-3 justify-center">
            <span className="px-3 py-1 text-[10px] font-semibold rounded-full bg-[#F1F1F4]">Anime</span>
            <span className="px-3 py-1 text-[10px] font-semibold rounded-full bg-[#F1F1F4]">Adventures</span>
          </div>
          <div className="flex items-center justify-between mt-3 text-[10px]">
            <span className="text-[#0041E6] font-bold flex items-center gap-1">
              <span className="w-3 h-3 bg-[#0041E6] rotate-45" /> GEMS 10000
            </span>
          </div>
        </div>
        <div className="flex-1 grid grid-cols-3 gap-1.5 px-3 pb-2">
          {["#FFF6E1", "#FFE9E9", "#E1F0FF", "#E9FFE1", "#FFE1F4", "#F1E1FF", "#FFFAD1", "#D1F4FF", "#FFD1D1"].map((c, i) => (
            <div key={i} className="aspect-square rounded-md" style={{ background: c }} />
          ))}
        </div>
        <div className="h-12 border-t border-black/5 flex items-center justify-around text-[9px] text-black/50">
          <span>Home</span><span>Voice</span><span>Basket</span><span>Account</span>
        </div>
      </div>
    </div>
  );
}

// CSS mock of eInsignts dashboard phone
function InsightsPhone() {
  return (
    <div className="relative w-[260px] md:w-[300px] aspect-[9/19] bg-black rounded-[36px] p-2 shadow-2xl">
      <div className="w-full h-full bg-white rounded-[28px] overflow-hidden flex flex-col p-4 text-black">
        <div className="flex items-center gap-2">
          <div className="w-9 h-9 rounded-full bg-[#0041E6]" />
          <div>
            <div className="text-[11px] font-bold">Andrew</div>
            <div className="text-[9px] text-black/60">Earnings: $ 10,000 CAD</div>
          </div>
        </div>
        <div className="mt-4 flex items-center gap-1">
          <span className="font-black italic text-[#0041E6]" style={{ fontFamily: "Inter", fontSize: 22 }}>e</span>
          <span className="font-black italic text-black" style={{ fontFamily: "Inter", fontSize: 16 }}>Insights</span>
        </div>
        <div className="mt-3 rounded-xl bg-[#F8F8FB] p-3">
          <div className="flex items-center justify-between text-[9px] text-black/60">
            <span>Total Earnings 2022</span>
            <span className="px-2 py-0.5 rounded-full bg-white border text-[8px]">10/1/22 — 10/30/22</span>
          </div>
          <div className="mt-2 flex items-end justify-between">
            <div>
              <div className="font-black text-[26px] leading-none">$ 10K</div>
              <div className="text-[9px] text-black/50">$ 10,124.73 CAD</div>
            </div>
            <div className="text-right">
              <div className="text-[10px] font-bold">$1,234.99</div>
              <div className="text-[9px] text-black/50">Current Month</div>
              <div className="text-[10px] text-green-600 font-bold">+10%</div>
            </div>
          </div>
        </div>
        <div className="mt-3 text-[10px] font-bold">Monthly Earnings 2022</div>
        <div className="mt-2 flex items-end gap-1 h-16">
          {[35, 50, 30, 65, 45, 70, 55, 80, 40, 60, 75, 50].map((h, i) => (
            <div key={i} className="flex-1 bg-[#0041E6] rounded-sm" style={{ height: `${h}%` }} />
          ))}
        </div>
        <div className="mt-auto h-2 rounded-full bg-[#0041E6]" />
        <div className="mt-3 flex justify-around text-[8px] text-black/50">
          <span>Listings</span><span>Messages</span><span>Add</span><span className="text-[#0041E6] font-bold">Insights</span><span>Licensee</span>
        </div>
      </div>
    </div>
  );
}

// CSS mock of premiere drop leaderboard phone
function LeaderboardPhone() {
  return (
    <div className="relative w-[260px] md:w-[300px] aspect-[9/19] bg-black rounded-[36px] p-2 shadow-2xl">
      <div className="w-full h-full bg-white rounded-[28px] overflow-hidden flex flex-col">
        <div className="bg-[#0041E6] px-4 py-3 flex items-center justify-between text-white">
          <span className="text-xs">‹</span>
          <div className="flex items-center gap-1">
            <span className="font-black text-[#FFD400] text-xs tracking-wider">PREMIERE DR</span>
            <Star size={10} className="text-[#FFD400] fill-[#FFD400]" />
            <span className="font-black text-[#FFD400] text-xs tracking-wider">P</span>
          </div>
          <span className="text-xs">?</span>
        </div>
        <div className="px-4 py-3 bg-white">
          <div className="flex gap-2">
            <div className="w-10 h-10 rounded bg-gradient-to-br from-pink-300 to-purple-400" />
            <div>
              <div className="text-[10px] font-bold leading-tight">Miku Concert Garfied — Youtooz Collectibles</div>
              <div className="text-[8px] text-black/60">by YouTooz</div>
              <span className="inline-block mt-1 px-2 py-0.5 bg-[#FFD400] text-[8px] font-bold rounded-full">Premiere Drop</span>
            </div>
          </div>
        </div>
        <div className="flex justify-center items-end gap-1 py-3 bg-white">
          {[
            { name: "Jackson", rank: 2, color: "#9747FF", h: 60 },
            { name: "Eiden", rank: 1, color: "#0041E6", h: 80 },
            { name: "Emma Aria", rank: 3, color: "#47FF7B", h: 50 },
          ].map((p, i) => (
            <div key={i} className="flex flex-col items-center" style={{ width: 60 }}>
              <div className="w-10 h-10 rounded-full border-2" style={{ borderColor: p.color, background: "#ddd" }} />
              <div className="font-black text-[10px] mt-1 text-white px-2 rounded-full" style={{ background: p.color }}>{p.rank}</div>
              <div className="text-[8px] mt-1 font-semibold leading-tight text-center">{p.name}<br />Gomez</div>
            </div>
          ))}
        </div>
        <div className="bg-[#FFD400] mx-3 rounded-lg p-2 flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-black/40" />
          <div className="flex-1">
            <div className="text-[10px] font-bold">Marcus Gomez</div>
            <div className="text-[8px]">You</div>
          </div>
          <div className="text-right">
            <div className="font-black text-sm">723</div>
            <div className="text-[7px]">Ranking</div>
          </div>
        </div>
        <div className="flex-1 px-3 py-2 space-y-1.5 overflow-hidden">
          {["Jason Gomez", "Anonymous", "Janice Gomez", "Cindy Gomez", "Jason"].map((n, i) => (
            <div key={i} className="flex items-center gap-2 text-[10px]">
              <div className="w-6 h-6 rounded-full bg-black/15" />
              <span className="flex-1 font-semibold">{n}</span>
              <span className="font-black text-black/60">{i + 4}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

// CSS mock of dashboard tablet (used twice — Sell & Get Paid + Manage Products)
function DashboardTablet({ variant = "insights" }) {
  return (
    <div className="relative w-full max-w-[520px] aspect-[4/3] bg-black rounded-[28px] p-2 shadow-2xl">
      <div className="w-full h-full bg-white rounded-[20px] overflow-hidden flex">
        <div className="w-[120px] bg-[#0041E6] text-white text-[11px] font-semibold p-4 flex flex-col gap-3">
          <img src={LOGO_SRC} alt="" className="h-7 w-auto brightness-0 invert" />
          <div className="mt-3 space-y-3">
            <div>Dashboard</div>
            <div className={variant === "products" ? "bg-white text-[#0041E6] -mx-4 px-4 py-1.5 font-bold rounded-r-full" : ""}>Product List</div>
            <div>Orders</div>
            <div className={variant === "insights" ? "bg-white text-[#0041E6] -mx-4 px-4 py-1.5 font-bold rounded-r-full" : ""}>Insights</div>
            <div>Assets</div>
            <div>Account</div>
          </div>
          <div className="mt-auto text-[9px] underline">Log out</div>
        </div>
        <div className="flex-1 p-4 text-black">
          <div className="w-full h-7 rounded-full bg-[#F1F1F4] flex items-center px-3 text-[9px] text-black/50 gap-2">
            <Search size={10} /> Search Products...
          </div>
          {variant === "insights" ? (
            <>
              <div className="mt-4 flex items-center gap-1">
                <span className="font-black italic text-[#0041E6]" style={{ fontFamily: "Inter", fontSize: 18 }}>e</span>
                <span className="font-black text-black" style={{ fontFamily: "Inter", fontSize: 14 }}>Insights</span>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="rounded-lg border border-black/10 p-2">
                  <div className="text-[8px] text-black/50">Lifetime Earnings</div>
                  <div className="font-black text-lg">$ 10K</div>
                  <div className="text-[8px] text-green-600 font-bold">+10%</div>
                </div>
                <div className="rounded-lg border border-black/10 p-2">
                  <div className="text-[8px] text-black/50">Monthly Earnings 2024</div>
                  <div className="flex items-end gap-0.5 h-8 mt-1">
                    {[40, 55, 35, 60, 45, 70, 50, 75, 40, 65, 55].map((h, i) => (
                      <div key={i} className="flex-1 bg-[#0041E6] rounded-sm" style={{ height: `${h}%` }} />
                    ))}
                  </div>
                </div>
              </div>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <div className="rounded-lg p-2 text-white" style={{ background: "#0041E6" }}>
                  <div className="text-[10px] font-black">DISNEY</div>
                  <div className="text-[8px] opacity-80">Total Earnings</div>
                  <div className="font-black text-base text-[#FFD400]">$ 5K</div>
                </div>
                <div className="rounded-lg p-2 text-white" style={{ background: "#E63946" }}>
                  <div className="text-[10px] font-black">WARNER</div>
                  <div className="text-[8px] opacity-80">Total Earnings</div>
                  <div className="font-black text-base text-[#FFD400]">$ 6K</div>
                </div>
              </div>
            </>
          ) : (
            <>
              <div className="mt-3 font-black text-[#0041E6] text-base">Product List</div>
              <div className="text-[8px] text-black/50">Organize and manage your inventory faster than ever</div>
              <div className="flex gap-3 mt-3 text-[9px] border-b border-black/10 pb-1">
                <span className="font-bold border-b-2 border-[#0041E6] pb-1">All products</span>
                <span className="text-black/50">Online</span>
                <span className="text-black/50">Offline</span>
                <span className="text-black/50">Flagged</span>
              </div>
              {[1, 2, 3, 4].map((i) => (
                <div key={i} className="flex items-center gap-2 py-1.5 text-[9px] border-b border-black/5">
                  <div className="w-6 h-6 rounded bg-black/10" />
                  <div className="flex-1">
                    <div className="font-bold">Adidas Originals × Disney</div>
                    <div className="text-black/50">SKU 123456</div>
                  </div>
                  <span>$99.99</span>
                  <span className="w-1.5 h-3 rounded-sm bg-green-500" />
                </div>
              ))}
            </>
          )}
        </div>
      </div>
    </div>
  );
}

export default function LicenseesPage() {
  return (
    <div data-testid="licensees-page" className="bg-[#FAFAFA]">

      {/* 1. HERO */}
      <section className="relative pt-12 md:pt-20 pb-20 overflow-hidden bg-white">
        {/* Comic Con background — subtle, blurred for readability */}
        <div
          className="absolute inset-0 -z-10 pointer-events-none"
          aria-hidden
          style={{
            backgroundImage: `url(${A.comicCon})`,
            backgroundSize: "cover",
            backgroundPosition: "center",
            opacity: 0.18,
            filter: "blur(2px)",
          }}
        />
        <div className="absolute inset-0 -z-10 bg-gradient-to-b from-white/85 via-white/75 to-white pointer-events-none" aria-hidden />
        <div className="absolute inset-0 -z-10 pointer-events-none">
          <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[#0041E6]/8 blur-3xl" />
          <div className="absolute top-1/2 -left-40 w-[500px] h-[500px] rounded-full bg-[#FFD93D]/20 blur-3xl" />
        </div>
        <div className="mx-auto max-w-[1400px] px-5 md:px-12 relative z-10">
          <motion.div {...fadeUp} className="mb-8">
            <Eyebrow>Get discovered, get paid, get control.</Eyebrow>
          </motion.div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <div className="lg:col-span-6">
              <motion.h1 {...fadeUp} className="font-display text-black leading-[0.9]" style={{ fontSize: "clamp(56px, 9vw, 140px)" }}>
                Connect<br />Licensees<br />to <span className="text-[#0041E6]">Fans.</span>
              </motion.h1>
              <motion.p {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }} className="mt-8 text-[18px] md:text-[20px] text-black/75 leading-relaxed max-w-2xl">
                Where all official licensed products and events are aggregated on a single miraculous app — directly connecting fans with the licensed products they want, in their licensed territory. Verified licensees of major studios like Disney, Warner Bros., and Paramount upload their products to sell and get paid.
              </motion.p>
              <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.2 }} className="mt-10 flex flex-wrap items-center gap-4">
                <a href={T.signUp} target="_blank" rel="noopener noreferrer" className="group bg-[#0041E6] hover:bg-[#0033B8] text-white font-medium text-[18px] rounded-full pl-7 pr-2 h-[56px] inline-flex items-center gap-3 shadow-[0_14px_30px_-12px_rgba(0,65,230,0.6)] transition" data-testid="hero-sign-up-btn">
                  Sign-Up
                  <span className="w-[40px] h-[40px] rounded-full bg-white text-[#0041E6] flex items-center justify-center transition-transform group-hover:rotate-45">
                    <ArrowUpRight size={18} />
                  </span>
                </a>
                <a href={T.onboardNow} target="_blank" rel="noopener noreferrer" className="border-2 border-[#0041E6] text-[#0041E6] hover:bg-[#0041E6] hover:text-white font-medium text-[18px] rounded-full px-7 h-[56px] inline-flex items-center justify-center transition" data-testid="onboard-now-btn">
                  Onboard Now
                </a>
                <a href="https://help.egeez.com/" target="_blank" rel="noopener noreferrer" className="text-black/70 hover:text-[#0041E6] underline-offset-4 hover:underline font-semibold text-[15px] inline-flex items-center gap-1 transition" data-testid="hero-resources-link">
                  Resources <ArrowUpRight size={14} />
                </a>
              </motion.div>
            </div>
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-6 flex flex-col items-center"
            >
              <motion.div
                {...fadeUp}
                transition={{ duration: 0.6, delay: 0.15 }}
                className="mb-4 inline-flex items-center gap-2 bg-[#FFD400] text-black text-[11px] md:text-[12px] font-black tracking-[0.22em] uppercase px-3.5 py-1.5 rounded-full shadow-md"
              >
                ✨ Right fan · Right place · Right time
              </motion.div>
              <LaptopVideo src={A.dashboardVideo} />
              {/* eLicensee logo under the laptop */}
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.4 }}
                className="mt-8 flex flex-col items-center gap-2"
              >
                <div className="text-[10px] font-bold tracking-[0.3em] uppercase text-black/40">
                  Vendor Portal
                </div>
                <img
                  src={A.eLicenseeLogo}
                  alt="eLicensee — eGeez vendor portal"
                  className="h-7 md:h-8 w-auto"
                  draggable={false}
                />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 3. WHY SELL ON eGeez */}
      <section className="relative py-24 md:py-32 bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-12 text-center">
          <motion.h2 {...fadeUp} className="font-display flex items-center justify-center flex-wrap gap-x-4 gap-y-2" style={{ fontSize: "clamp(48px, 7vw, 110px)", lineHeight: 0.9 }}>
            <span className="text-black">Why Sell on</span>
            <img src={LOGO_SRC} alt="eGeez" className="inline-block h-[60px] md:h-[90px] w-auto" />
            <span className="text-black">?</span>
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.15 }}
            className="mt-8 inline-flex items-start gap-3 max-w-3xl mx-auto text-left"
          >
            <span className="inline-flex items-center justify-center flex-shrink-0 w-7 h-7 rounded-full bg-[#0041E6] mt-1 shadow-md">
              <Check size={16} className="text-white" strokeWidth={3} />
            </span>
            <span className="text-[16px] md:text-[18px] text-black/85 leading-relaxed">
              Every official licensee is <strong className="text-[#0041E6]">eGeez Verified</strong> with a blue checkmark — confirming authenticity and fan trust.
            </span>
          </motion.p>
        </div>
      </section>

      {/* 4. DIRECT ACCESS TO FANS */}
      <section className="relative pb-20 bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-12">
          <motion.div {...fadeUp} className="rounded-[40px] overflow-hidden grid grid-cols-1 md:grid-cols-2" style={{ background: "#F08080" }}>
            <div className="p-10 md:p-14 text-white flex flex-col justify-center">
              <h3 className="font-display text-[40px] md:text-[56px] leading-[0.95]">Direct Access to Fans</h3>
              <p className="mt-6 text-[16px] md:text-[18px] leading-relaxed">
                The eGeez app makes it easier to reach the right fans purchasing authentic licensed products in one unified platform — not fragmented across retailers. eGeez connects fans with you, the official licensee, and a wide range of licensed merchandise from toys to apparel to food and beverages.
              </p>
              <div className="mt-8">
                <a
                  href={T.signUp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="direct-access-sign-up-btn"
                  className="group inline-flex items-center gap-3 bg-white text-[#F08080] font-bold text-[15px] rounded-full pl-5 pr-1.5 h-[46px] transition shadow-lg hover:bg-[#FFD400] hover:text-black"
                >
                  Sign-Up
                  <span className="w-[36px] h-[36px] rounded-full bg-[#F08080] text-white flex items-center justify-center transition-transform group-hover:rotate-45">
                    <ArrowUpRight size={16} />
                  </span>
                </a>
              </div>
            </div>
            <div className="relative min-h-[360px] bg-black overflow-hidden">
              <img
                src={A.paulScardino}
                alt="@ThePOPthusiast — 3× Guinness World Records, largest Funko Pop! Collection"
                className="absolute inset-0 w-full h-full object-cover"
                draggable={false}
              />
              {/* gradient overlay for caption readability */}
              <div className="absolute inset-x-0 bottom-0 h-24 bg-gradient-to-t from-black/75 via-black/30 to-transparent pointer-events-none" aria-hidden />
              <div className="absolute bottom-4 left-4 right-4 text-[13px] md:text-[14px] font-semibold text-white drop-shadow-md">
                <span className="inline-flex items-center gap-2 bg-black/55 backdrop-blur-sm rounded-full px-3 py-1.5">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#FFD400]" />
                  @ThePOPthusiast · 3× Guinness World Records — largest Funko Pop! Collection
                </span>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 5. MAXIMIZE PROFITS + DON'T PURGE INVENTORY */}
      <section className="relative pb-20 bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1400px] px-5 md:px-12 grid grid-cols-1 md:grid-cols-2 gap-6">
          <motion.div {...fadeUp} className="rounded-[40px] p-10 md:p-12 text-white relative overflow-hidden" style={{ background: "#5B2EBE" }}>
            <div className="inline-flex items-center gap-2 mb-4 bg-[#FFD400] text-[#5B2EBE] text-[10px] md:text-[11px] font-black tracking-[0.22em] uppercase px-3 py-1.5 rounded-full">
              Payouts · Licensee <ArrowRight size={10} strokeWidth={3} /> Fan
            </div>
            <h3 className="font-display text-[40px] md:text-[52px] leading-[0.95]">Maximize Profits</h3>
            <div className="mt-3 text-[13px] md:text-[14px] font-bold tracking-[0.18em] uppercase text-[#FFD400]">
              Sell at Higher Margins · Ship Direct · Take Control
            </div>
            <p className="mt-5 text-[15px] md:text-[16px] leading-relaxed text-white/90 max-w-md">
              Sell &amp; get paid. Accept fan orders, generate per-licensor sales reports, and withdraw your payouts — returns and refunds handled for a seamless experience. <em className="text-[#FFD400] not-italic font-semibold">Auto-Royalty Reconciliation coming soon.</em> We get paid, when you get paid. Zero monthly charges. Unlimited product uploads.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5">
              <span className="text-[9px] font-bold tracking-[0.25em] uppercase text-white/70">Powered by</span>
              <img src={A.eLicenseeLogo} alt="eLicensee" className="h-4 w-auto brightness-0 invert opacity-90" draggable={false} />
            </div>

            <div className="mt-8">
              <PayoutsCard />
            </div>

            {/* decorative */}
            <div className="absolute -top-20 -right-20 w-[320px] h-[320px] rounded-full bg-white/5 blur-3xl pointer-events-none" aria-hidden />
          </motion.div>

          <motion.div {...fadeUp} transition={{ duration: 0.6, delay: 0.1 }} className="rounded-[40px] p-10 md:p-12 text-white relative overflow-hidden" style={{ background: "#0041E6" }}>
            <div className="inline-flex items-center gap-2 mb-4 bg-[#FFD400] text-[#0041E6] text-[10px] md:text-[11px] font-black tracking-[0.22em] uppercase px-3 py-1.5 rounded-full">
              D2F · Direct-to-Fan
            </div>
            <h3 className="font-display text-[34px] md:text-[44px] leading-[0.95]">
              Don&apos;t Purge Inventory—<br />Sell It to Real Fans
            </h3>
            <p className="mt-5 text-[15px] md:text-[16px] leading-relaxed max-w-md text-white/90">
              You don&apos;t need to panic-sell or purge when your license ends. eGeez connects your inventory to verified fans in your territory, helping you sell faster and cleanly — royalties go to the licensor, fans get what they want, you get paid, and everyone&apos;s happy.
            </p>
            <div className="mt-5 inline-flex items-center gap-2 bg-white/10 backdrop-blur-sm rounded-full px-3 py-1.5">
              <span className="text-[9px] font-bold tracking-[0.25em] uppercase text-white/70">Managed via</span>
              <img src={A.eLicenseeLogo} alt="eLicensee" className="h-4 w-auto brightness-0 invert opacity-90" draggable={false} />
            </div>

            <div className="mt-8">
              <InventoryAlertCard />
            </div>

            {/* decorative */}
            <div className="absolute -top-20 -right-20 w-[320px] h-[320px] rounded-full bg-white/5 blur-3xl pointer-events-none" aria-hidden />
          </motion.div>
        </div>
      </section>

      {/* 6. GEOFENCING LICENSED PRODUCTS — dark green */}
      <section className="relative pb-24 bg-[#FAFAFA]" data-testid="geofencing-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-12">
          <motion.div {...fadeUp} className="rounded-[40px] overflow-hidden grid grid-cols-1 md:grid-cols-5 min-h-[520px]" style={{ background: "#1F3933" }}>
            {/* LEFT — BIG "We can't ship" checkout image + flying evil emojis + alerts */}
            <div className="relative md:col-span-3 bg-gradient-to-br from-[#FFB347] to-[#FF6B6B] flex items-center justify-center p-6 md:p-10 overflow-hidden min-h-[420px]">
              {/* Flying evil emojis */}
              <motion.span
                aria-hidden
                animate={{ x: [-20, 20, -20], y: [0, -8, 0], rotate: [-10, 10, -10] }}
                transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-6 left-6 text-4xl md:text-5xl select-none pointer-events-none z-20"
              >
                👿
              </motion.span>
              <motion.span
                aria-hidden
                animate={{ x: [10, -15, 10], y: [-6, 8, -6], rotate: [8, -8, 8] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                className="absolute top-14 right-10 text-3xl md:text-4xl select-none pointer-events-none z-20"
              >
                😈
              </motion.span>
              <motion.span
                aria-hidden
                animate={{ y: [0, -14, 0], rotate: [-6, 6, -6] }}
                transition={{ duration: 3.2, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                className="absolute bottom-8 left-12 text-3xl select-none pointer-events-none z-20"
              >
                🚫
              </motion.span>

              {/* Red alert pulsing stripe */}
              <motion.div
                animate={{ opacity: [0.6, 1, 0.6] }}
                transition={{ duration: 1.8, repeat: Infinity, ease: "easeInOut" }}
                className="absolute top-4 left-1/2 -translate-x-1/2 z-20 bg-[#E63946] text-white text-[10px] font-black tracking-[0.24em] uppercase px-3 py-1.5 rounded-full shadow-lg flex items-center gap-2"
              >
                <AlertTriangle size={12} strokeWidth={3} />
                Territorial Breach Blocked
              </motion.div>

              {/* Yellow warning */}
              <motion.div
                animate={{ y: [0, -4, 0] }}
                transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                className="absolute bottom-5 right-5 z-20 bg-[#FFD400] text-black text-[10px] font-black tracking-[0.18em] uppercase px-3 py-1.5 rounded-full shadow-md flex items-center gap-1.5"
              >
                ⚠️ Region-locked
              </motion.div>

              {/* BIG Checkout screenshot — centered, floating */}
              <motion.div
                animate={{ y: [0, -10, 0] }}
                transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                className="relative z-[5] w-full max-w-[560px]"
              >
                <div className="absolute -inset-6 bg-white/30 blur-3xl rounded-3xl" aria-hidden />
                <img
                  src={A.cannotShip}
                  alt="We can't ship this item to your region — geofenced checkout"
                  className="relative w-full h-auto rounded-2xl shadow-[0_40px_80px_-20px_rgba(0,0,0,0.55)] ring-1 ring-black/10"
                  draggable={false}
                />
              </motion.div>
            </div>

            {/* RIGHT — copy + smaller GeoShield */}
            <div className="relative md:col-span-2 p-10 md:p-12 text-white flex flex-col justify-center overflow-hidden">
              <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] font-black tracking-[0.28em] uppercase text-[#FFD400] mb-4">
                <span className="inline-block w-6 h-px bg-[#FFD400]" />
                GeoShield · Region Aware
              </div>

              <h3 className="font-display italic text-[32px] md:text-[44px] leading-[0.95]">Geofencing Licensed Products</h3>

              {/* Discoverability Unlocked — headline accent */}
              <motion.div
                initial={{ opacity: 0, x: -10 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6, delay: 0.2 }}
                className="mt-3 font-display italic text-[24px] md:text-[32px] leading-[1] text-[#FFD400]"
              >
                Discoverability Unlocked.
              </motion.div>

              {/* Smaller GeoShield — sits inline */}
              <motion.div
                initial={{ opacity: 0, scale: 0.85, rotate: -10 }}
                whileInView={{ opacity: 1, scale: 1, rotate: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.8, delay: 0.3, ease: [0.16, 1, 0.3, 1] }}
                className="mt-6 relative w-[90px] md:w-[110px] pointer-events-none select-none"
              >
                <div className="absolute -inset-3 bg-[#FFD400]/30 blur-2xl rounded-full" aria-hidden />
                <motion.img
                  src={A.geoShield}
                  alt="eGeez GeoShield"
                  animate={{ y: [0, -5, 0], rotate: [-3, 3, -3] }}
                  transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
                  className="relative w-full h-auto drop-shadow-[0_20px_40px_rgba(255,212,0,0.45)]"
                  draggable={false}
                />
              </motion.div>

              <p className="mt-6 text-[14px] md:text-[15px] leading-relaxed text-white/90">
                By capturing exact licensed territories from your agreements, eGeez geolocates official licensed products so they only appear to fans in approved regions. Licensors safeguarded. Fans served localized, seamless shopping.
              </p>

              <div className="mt-8">
                <a
                  href={T.signUp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="geofencing-sign-up-btn"
                  className="group inline-flex items-center gap-3 bg-[#FFD400] hover:bg-white text-black font-bold text-[15px] rounded-full pl-5 pr-1.5 h-[46px] transition shadow-lg"
                >
                  Sign-Up for GeoShield
                  <span className="w-[36px] h-[36px] rounded-full bg-black text-[#FFD400] flex items-center justify-center transition-transform group-hover:rotate-45">
                    <ArrowUpRight size={16} />
                  </span>
                </a>
              </div>
            </div>
          </motion.div>
        </div>

        {/* BIG headline statement on white space below */}
        <div className="mx-auto max-w-[1400px] px-5 md:px-12 mt-20 md:mt-28">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="text-center"
            data-testid="fan-located-statement"
          >
            <div className="inline-flex items-center gap-2 text-[10px] md:text-[11px] font-black tracking-[0.32em] uppercase text-[#0041E6] mb-6">
              <span className="w-6 h-px bg-[#0041E6]" />
              Match Made in Merch
              <span className="w-6 h-px bg-[#0041E6]" />
            </div>
            <h2
              className="font-display text-black leading-[0.92] mx-auto"
              style={{ fontSize: "clamp(40px, 7vw, 96px)", maxWidth: "1100px" }}
            >
              Fan.{" "}
              <span className="italic text-[#0041E6]">Located.</span>
              <br />
              Like finding your perfect match —{" "}
              <span className="italic" style={{ color: "#FFB800" }}>but for merch.</span>
            </h2>
          </motion.div>
        </div>
      </section>

      {/* 7. IT'S A BIG NO — redesigned: hero title with flying NO emojis, then card row */}
      <section className="relative py-24 md:py-32 bg-white overflow-hidden" data-testid="big-no-section">
        {/* Flying NO/thumbs-down emojis */}
        {[
          { e: "🚫", x: "6%", y: "18%", d: 0, dur: 5 },
          { e: "❌", x: "14%", y: "68%", d: 0.8, dur: 6 },
          { e: "👎", x: "88%", y: "22%", d: 0.3, dur: 5.5 },
          { e: "🙅", x: "92%", y: "70%", d: 1.2, dur: 4.8 },
          { e: "⛔", x: "50%", y: "82%", d: 1.6, dur: 5.4 },
          { e: "🚷", x: "46%", y: "10%", d: 2, dur: 6.2 },
        ].map((emo, i) => (
          <motion.span
            key={i}
            aria-hidden
            initial={{ opacity: 0, scale: 0.6 }}
            animate={{
              opacity: [0, 1, 1, 0.7],
              y: [0, -24, 0, -12],
              rotate: [-14, 14, -10, 10],
              scale: [0.9, 1.05, 0.95, 1],
            }}
            transition={{ duration: emo.dur, repeat: Infinity, ease: "easeInOut", delay: emo.d }}
            style={{ left: emo.x, top: emo.y }}
            className="absolute text-3xl md:text-4xl select-none pointer-events-none"
          >
            {emo.e}
          </motion.span>
        ))}

        <div className="mx-auto max-w-[1200px] px-5 md:px-12 relative">
          {/* Hero title */}
          <motion.div {...fadeUp} className="text-center">
            <div className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.32em] uppercase text-[#0041E6] mb-5">
              <span className="w-6 h-px bg-[#0041E6]" />
              What you won't find here
              <span className="w-6 h-px bg-[#0041E6]" />
            </div>
            <h2
              className="font-display text-black leading-[0.85] inline-flex items-center justify-center gap-4 md:gap-6 flex-wrap"
              style={{ fontSize: "clamp(64px, 11vw, 172px)" }}
            >
              <span>It&apos;s a</span>
              <motion.span
                animate={{ rotate: [-6, 6, -6], y: [0, -6, 0] }}
                transition={{ duration: 2.2, repeat: Infinity, ease: "easeInOut" }}
                className="inline-flex items-center justify-center rounded-full bg-black text-[#FFD400] px-8 md:px-12 py-2 md:py-3 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.45)]"
              >
                BIG
              </motion.span>
              <span className="italic text-[#0041E6]">&ldquo;No&rdquo;</span>
              <motion.span
                animate={{ rotate: [8, -12, 8], y: [0, -4, 0] }}
                transition={{ duration: 2.6, repeat: Infinity, ease: "easeInOut" }}
                className="inline-block text-[0.6em]"
              >
                👎
              </motion.span>
            </h2>
            <p className="mt-8 text-[16px] md:text-[18px] text-black/70 max-w-2xl mx-auto leading-relaxed">
              No catches. No surprises. Just a platform that only wins when you win.
            </p>
          </motion.div>

          {/* 4-card row — evenly spaced, stagger entrance */}
          <div className="mt-16 md:mt-20 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 md:gap-6">
            {[
              { t: "No Upload Fees", b: "Upload unlimited products at no cost. Let's create miracles.", bg: "#F08080" },
              { t: "No Monthly Fees", b: "Zero monthly charges. Hassle-free selling from day one.", bg: "#FF4DA6" },
              { t: "We get paid only when you sell.", b: "We save the world together — eGeez only earns when you earn.", bg: "#FFA500" },
              { t: "No Hidden Fees", b: "What you see is what you get. One transparent commission for all.", bg: "#1FB8DA" },
            ].map((c, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 24, rotate: (i % 2 === 0 ? -2 : 2) }}
                whileInView={{ opacity: 1, y: 0, rotate: 0 }}
                viewport={{ once: true, margin: "-40px" }}
                whileHover={{ y: -6, rotate: (i % 2 === 0 ? -1.5 : 1.5) }}
                transition={{ duration: 0.55, delay: i * 0.08, ease: [0.16, 1, 0.3, 1] }}
                className="relative rounded-[28px] p-7 text-white shadow-[0_20px_40px_-18px_rgba(0,0,0,0.25)]"
                style={{ background: c.bg }}
              >
                <div className="absolute top-5 right-5 w-9 h-9 rounded-full bg-black/15 flex items-center justify-center">
                  <XIcon size={16} className="text-white" strokeWidth={3} />
                </div>
                <div className="text-[10px] font-black tracking-[0.22em] uppercase text-white/80 mb-3">
                  No · 0{i + 1}
                </div>
                <h4 className="font-display text-[26px] md:text-[30px] leading-[1.02]">{c.t}</h4>
                <p className="mt-3 text-[14px] md:text-[15px] leading-snug text-white/95">{c.b}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* 8. PREMIERE DROP — mirrors For Fans design (white top + yellow bottom), with sparkle & flying stars */}
      <section className="relative py-24 md:py-32 bg-[#FAFAFA] overflow-hidden" id="premiere-drop-licensees" data-testid="premiere-drop-licensees">
        {/* Flying stars across the whole section */}
        {[
          { x: "4%", y: "8%", s: 16, d: 0, dur: 4 },
          { x: "12%", y: "52%", s: 12, d: 0.6, dur: 5 },
          { x: "22%", y: "88%", s: 14, d: 1.2, dur: 4.4 },
          { x: "48%", y: "6%", s: 18, d: 0.3, dur: 4.8 },
          { x: "62%", y: "94%", s: 12, d: 1, dur: 5.2 },
          { x: "78%", y: "12%", s: 16, d: 0.5, dur: 4.6 },
          { x: "86%", y: "58%", s: 14, d: 1.4, dur: 4.2 },
          { x: "94%", y: "86%", s: 12, d: 0.8, dur: 5 },
        ].map((st, i) => (
          <motion.span
            key={i}
            aria-hidden
            style={{ left: st.x, top: st.y }}
            animate={{
              opacity: [0.4, 1, 0.4],
              scale: [0.8, 1.15, 0.8],
              rotate: [0, 180, 360],
            }}
            transition={{ duration: st.dur, repeat: Infinity, ease: "easeInOut", delay: st.d }}
            className="absolute text-[#FFD400] pointer-events-none select-none"
          >
            <Sparkles size={st.s} fill="#FFD400" strokeWidth={1.5} />
          </motion.span>
        ))}

        <div className="mx-auto max-w-[1300px] px-5 md:px-12 space-y-6 relative">
          {/* TOP HALF — WHITE bg */}
          <motion.div
            {...fadeUp}
            className="relative rounded-[40px] overflow-hidden p-10 md:p-16 text-black bg-white border border-black/8 shadow-[0_30px_80px_-30px_rgba(0,0,0,0.18)]"
          >
            <div className="absolute -top-32 -right-40 w-[600px] h-[600px] rounded-full bg-[#FFD400]/15 blur-3xl pointer-events-none" />

            <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
              {/* LEFT — Copy (kept exact licensees wording) */}
              <div className="lg:col-span-7">
                <div className="inline-flex items-center gap-2 text-[11px] md:text-[12px] font-bold tracking-[0.28em] uppercase text-[#0041E6] mb-5">
                  <span className="inline-block w-6 h-px bg-[#0041E6]" />
                  Onboard early. Earn early
                </div>
                <img
                  src={A.premiereLogo}
                  alt="Premiere Drop"
                  className="h-14 md:h-20 w-auto mb-6"
                  draggable={false}
                />
                <h2
                  className="font-display text-black leading-[0.92]"
                  style={{ fontSize: "clamp(40px, 6.5vw, 88px)" }}
                >
                  Onboard early.<br />
                  <span className="italic text-[#0041E6]">Earn early.</span>
                </h2>
                <p className="mt-6 text-[16px] md:text-[18px] text-black/80 leading-relaxed max-w-xl">
                  This feature acts like a <strong>PRE-ORDER</strong> when a product is freshly approved by the licensor before manufacturing — but with a twist. <strong>Like a movie hits theatres</strong> before streaming, your licensed merch premieres on eGeez before mass distribution.
                </p>

                <div className="mt-8 flex flex-wrap items-center gap-4">
                  <a
                    href={T.signUp}
                    target="_blank"
                    rel="noopener noreferrer"
                    data-testid="premiere-sign-up-btn"
                    className="group inline-flex items-center gap-3 bg-[#0041E6] hover:bg-[#0033B8] text-white font-bold text-[17px] rounded-full pl-7 pr-2 h-[58px] transition shadow-[0_14px_30px_-12px_rgba(0,65,230,0.5)]"
                  >
                    Sign-Up
                    <span className="w-[42px] h-[42px] rounded-full bg-white text-[#0041E6] flex items-center justify-center transition-transform group-hover:rotate-45">
                      <ArrowUpRight size={18} />
                    </span>
                  </a>
                  <a
                    href={T.onboardNow}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 border-2 border-[#0041E6] text-[#0041E6] hover:bg-[#0041E6] hover:text-white font-semibold text-[16px] rounded-full px-6 h-[58px] transition"
                  >
                    Onboard Now
                  </a>
                </div>
              </div>

              {/* RIGHT — Leaderboard phone w/ animated callout */}
              <motion.div
                initial={{ opacity: 0, scale: 0.92, y: 20 }}
                whileInView={{ opacity: 1, scale: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
                className="lg:col-span-5 flex justify-center"
              >
                <div className="group relative cursor-pointer" tabIndex={0} data-testid="licensee-leaderboard-phone">
                  <img
                    src={A.premiereLeaderboard}
                    alt="Premiere Drop leaderboard"
                    className="w-full max-w-[420px] rounded-[28px] shadow-[0_30px_80px_-20px_rgba(0,0,0,0.35)] transition-transform duration-500 group-hover:scale-[1.02]"
                    draggable={false}
                  />
                  <div className="absolute -top-4 -left-4 bg-black text-[#FFD400] text-[10px] font-black tracking-[0.25em] uppercase px-3 py-1.5 rounded-full rotate-[-6deg] shadow-lg">
                    ★ Leaderboard Live
                  </div>

                  <div
                    className="absolute -right-6 top-12 md:-right-12 md:top-16 z-20 origin-bottom-left opacity-0 -translate-y-3 scale-90 rotate-[-6deg] group-hover:opacity-100 group-hover:translate-y-0 group-hover:scale-100 group-hover:rotate-[-3deg] group-focus-within:opacity-100 group-focus-within:translate-y-0 group-focus-within:scale-100 transition-all duration-500"
                    aria-hidden
                  >
                    <div className="relative bg-[#FFD400] text-black px-5 py-3 rounded-2xl shadow-[0_18px_30px_-12px_rgba(0,0,0,0.45)] border-2 border-black">
                      <div className="flex items-center gap-2">
                        <span className="text-[20px] leading-none">★</span>
                        <div className="font-display italic text-[20px] md:text-[24px] leading-tight">
                          I Geez&apos;d it<br />
                          <span className="text-[#0041E6]">Third!</span>
                        </div>
                      </div>
                      <div className="absolute -bottom-2 left-6 w-4 h-4 bg-[#FFD400] border-r-2 border-b-2 border-black rotate-45" />
                    </div>
                    <Sparkles size={14} className="absolute -top-3 -right-3 text-[#0041E6] animate-pulse" />
                    <Sparkles size={12} className="absolute -bottom-2 -right-2 text-[#FFD400]" />
                  </div>

                  <div className="absolute left-1/2 -translate-x-1/2 -bottom-4 bg-black text-white text-[10px] font-bold tracking-[0.25em] uppercase px-3 py-1.5 rounded-full opacity-80 group-hover:opacity-0 transition-opacity">
                    Tap to brag
                  </div>
                </div>
              </motion.div>
            </div>
          </motion.div>

          {/* BOTTOM HALF — YELLOW bg (Create hype early) */}
          <motion.div
            {...fadeUp}
            transition={{ duration: 0.6, delay: 0.05 }}
            className="relative rounded-[40px] overflow-hidden p-10 md:p-16"
            style={{
              background:
                "radial-gradient(ellipse at top right, #FFE680 0%, #FFD400 35%, #F5B800 80%)",
            }}
          >
            {/* Star confetti */}
            <svg
              aria-hidden
              className="absolute inset-0 w-full h-full pointer-events-none opacity-20"
              viewBox="0 0 1200 600"
              preserveAspectRatio="xMidYMid slice"
            >
              {[
                [120, 80, 12], [340, 140, 8], [560, 90, 10], [820, 160, 14],
                [1080, 110, 9], [180, 320, 7], [420, 380, 11], [950, 360, 8],
                [1140, 480, 10], [70, 540, 13], [310, 520, 9], [580, 490, 11],
              ].map(([x, y, r], i) => (
                <g key={i} transform={`translate(${x} ${y})`}>
                  <path
                    d={`M0,-${r} L${r * 0.3},-${r * 0.3} L${r},0 L${r * 0.3},${r * 0.3} L0,${r} L-${r * 0.3},${r * 0.3} L-${r},0 L-${r * 0.3},-${r * 0.3} Z`}
                    fill="#000000"
                  />
                </g>
              ))}
            </svg>

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 text-[11px] font-bold tracking-[0.28em] uppercase text-[#0041E6] mb-4">
                <span className="inline-block w-6 h-px bg-[#0041E6]" />
                Create hype early
              </div>
              <h3
                className="font-display text-black leading-[0.95] max-w-4xl"
                style={{ fontSize: "clamp(32px, 4.5vw, 64px)" }}
              >
                Fans become <span className="italic text-[#0041E6]">Fanvestors.</span>
              </h3>
              <p className="mt-6 text-[16px] md:text-[18px] leading-relaxed max-w-3xl text-black/85">
                Capture <strong>early pre-orders</strong> before global releases (like box office pre-sales). Build loyalty through the <strong>&ldquo;fanvestor&rdquo;</strong> model and limited collectibles featured on the <strong>leaderboard</strong>. Screenshot and share your ranking!
              </p>
              <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-5 max-w-5xl">
                {[
                  { t: "Pre-orders before drop", b: "Lock in demand before the global release." },
                  { t: "Leaderboard hype", b: "Limited collectibles nobody else can get." },
                  { t: "Built-in virality", b: "Fans screenshot their ranking — free distribution." },
                ].map((c, i) => (
                  <div key={i} className="bg-white rounded-[20px] p-5 border border-black/5 shadow-sm">
                    <div className="text-[#0041E6] text-[20px] mb-2" aria-hidden>★</div>
                    <div className="font-semibold text-black text-[15px]">{c.t}</div>
                    <div className="text-[14px] text-black/70 mt-1 leading-snug">{c.b}</div>
                  </div>
                ))}
              </div>
              <p className="mt-10 font-display italic text-black text-[22px] md:text-[28px] leading-snug max-w-2xl">
                The early licensee captures the hype.<br />
                <span className="text-black/55">Everyone else chases the restock.</span>
              </p>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 9. eINSIGHTS — Source of Truth */}
      <section className="relative py-24 md:py-32 bg-white overflow-hidden" data-testid="einsights-section">
        <div className="mx-auto max-w-[1400px] px-5 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
            <motion.div {...fadeUp} className="lg:col-span-6">
              {/* Giant eInsights logo */}
              <div className="flex items-center gap-2 leading-none">
                <span
                  className="font-black italic text-[#0041E6]"
                  style={{ fontFamily: "Inter", fontSize: "clamp(80px, 11vw, 160px)", letterSpacing: "-0.04em" }}
                >
                  e
                </span>
                <span
                  className="font-black italic text-black"
                  style={{ fontFamily: "Inter", fontSize: "clamp(60px, 8vw, 120px)", letterSpacing: "-0.02em" }}
                >
                  Insights
                </span>
              </div>

              {/* Coming Soon — small, animated */}
              <motion.div
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.2 }}
                className="mt-5 inline-flex items-center gap-2 bg-black text-[#FFD400] text-[11px] font-black tracking-[0.3em] uppercase px-3 py-1.5 rounded-full shadow-lg"
              >
                <motion.span
                  animate={{ rotate: [0, 25, -25, 0] }}
                  transition={{ duration: 2.4, repeat: Infinity, ease: "easeInOut" }}
                  className="inline-block"
                >
                  🛠️
                </motion.span>
                Coming Soon
                <motion.span
                  animate={{ y: [0, -3, 0] }}
                  transition={{ duration: 1.6, repeat: Infinity, ease: "easeInOut", delay: 0.3 }}
                  className="inline-block"
                >
                  🧰
                </motion.span>
              </motion.div>

              <h3
                className="mt-8 font-display text-black leading-[0.95]"
                style={{ fontSize: "clamp(36px, 5vw, 64px)" }}
              >
                Source of Truth,<br />
                <span className="italic text-[#0041E6]">Finally!</span>
              </h3>
              <p className="mt-4 text-[16px] md:text-[18px] text-black/80 leading-relaxed max-w-xl">
                <strong>eInsights.</strong> Census-level data.
              </p>
              <p className="mt-3 text-[16px] md:text-[18px] text-black/70 leading-relaxed max-w-xl">
                AI market intelligence replacing guesswork with real data. Which licensee sold most. Which toy moved fastest. In which territory. To which fan. And why.
              </p>

              {/* Tool emojis row */}
              <div className="mt-6 flex items-center gap-3 text-[28px] md:text-[32px]">
                {["📊", "🤖", "📈", "🧠", "🎯"].map((e, i) => (
                  <motion.span
                    key={i}
                    aria-hidden
                    animate={{ y: [0, -6, 0], rotate: [-4, 4, -4] }}
                    transition={{ duration: 3 + i * 0.3, repeat: Infinity, ease: "easeInOut", delay: i * 0.15 }}
                    className="inline-block select-none"
                  >
                    {e}
                  </motion.span>
                ))}
              </div>

              <div className="mt-8">
                <a
                  href={T.signUp}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="einsights-sign-up-btn"
                  className="group inline-flex items-center gap-3 bg-[#0041E6] hover:bg-[#0033B8] text-white font-semibold text-[16px] rounded-full pl-6 pr-2 h-[52px] transition shadow-[0_14px_30px_-12px_rgba(0,65,230,0.5)]"
                >
                  Sign-Up to Get Early Access
                  <span className="w-[40px] h-[40px] rounded-full bg-white text-[#0041E6] flex items-center justify-center transition-transform group-hover:rotate-45">
                    <ArrowUpRight size={18} />
                  </span>
                </a>
              </div>
            </motion.div>

            {/* RIGHT — Animated tablet */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="lg:col-span-6 flex flex-col items-center"
            >
              <motion.div
                animate={{ y: [0, -10, 0], rotate: [-1.5, 1.5, -1.5] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="relative w-full max-w-[520px]"
              >
                <div className="absolute -inset-10 bg-[#0041E6]/15 blur-[80px] rounded-full pointer-events-none" aria-hidden />

                {/* Floating sparkles around tablet */}
                <motion.span
                  aria-hidden
                  animate={{ opacity: [0.4, 1, 0.4], scale: [0.8, 1.1, 0.8] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-4 -right-4 z-20 text-[#FFD400]"
                >
                  <Sparkles size={32} fill="#FFD400" />
                </motion.span>
                <motion.span
                  aria-hidden
                  animate={{ opacity: [0.6, 1, 0.6], scale: [0.9, 1.2, 0.9] }}
                  transition={{ duration: 3.6, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                  className="absolute -bottom-5 -left-5 z-20 text-[#0041E6]"
                >
                  <Sparkles size={24} fill="#0041E6" />
                </motion.span>

                <DashboardTablet variant="insights" />
              </motion.div>
              {/* Managed by eLicensee */}
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: 0.3 }}
                className="mt-8 inline-flex items-center gap-2 bg-black/5 rounded-full px-3.5 py-1.5"
              >
                <span className="text-[10px] font-bold tracking-[0.28em] uppercase text-black/50">Managed by</span>
                <img src={A.eLicenseeLogo} alt="eLicensee" className="h-5 w-auto" draggable={false} />
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* 10 & 11. EASY 3 STEPS + SPECIAL PERKS — cinematic vertical reveal */}
      <section className="relative py-28 md:py-40 bg-white overflow-hidden" data-testid="easy-three-steps">
        {/* Soft background accents */}
        <div className="absolute inset-0 -z-0 pointer-events-none" aria-hidden>
          <div className="absolute top-1/4 -left-40 w-[600px] h-[600px] rounded-full bg-[#0041E6]/5 blur-[120px]" />
          <div className="absolute bottom-1/4 -right-40 w-[600px] h-[600px] rounded-full bg-[#FFD400]/8 blur-[120px]" />
        </div>

        {/* Section header */}
        <div className="mx-auto max-w-[1200px] px-5 md:px-12 text-center mb-20 md:mb-28 relative">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
          >
            <div className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.32em] uppercase text-[#0041E6] mb-5">
              <span className="w-6 h-px bg-[#0041E6]" />
              Onboarding
              <span className="w-6 h-px bg-[#0041E6]" />
            </div>
            <h2
              className="font-display text-black leading-[0.9]"
              style={{ fontSize: "clamp(48px, 8vw, 120px)" }}
            >
              From signup to<br />
              <span className="italic text-[#0041E6]">selling, in 3 steps.</span>
            </h2>
          </motion.div>
        </div>

        {/* STEPS */}
        <div className="mx-auto max-w-[1200px] px-5 md:px-12 relative">
          {[
            {
              eyebrow: "Step 01",
              title: "Get Your ePIN. Instantly.",
              emoji: ["⚡", "🔐", "🤝"],
              lines: [
                { strong: "You must be an official licensee to join.", rest: " Hit Sign Up on the eLicensee portal — that's where the magic starts." },
                { strong: "Behind the scenes?", rest: " eGeez's eSmart Gateway combines human expertise, AI verification, and years of proprietary licensee domain intelligence to authenticate you in seconds." },
                { strong: "Already on our radar?", rest: " Your ePIN arrives instantly — no waiting, no friction." },
                { strong: "New to us?", rest: " Same-day verification. We'll find you in your inbox." },
                { strong: "Sometimes eGeez already knows who you are. 😉", rest: "" },
              ],
              accent: "#0041E6",
              side: "left",
            },
            {
              eyebrow: "Step 02",
              title: "Set Up. Ready to Sell. 2 Minutes.",
              emoji: ["🚀", "🗺️", "💳"],
              lines: [
                { strong: "Your progress bar is your launchpad —", rest: " quick, intuitive, done." },
                { strong: "Add your licensors under My Licensors.", rest: " Define your licensed territories so your products appear exactly where you're authorized to sell — geofenced, compliant, precise." },
                { strong: "Set up your account to get paid.", rest: "" },
                { strong: "That's it. You're live-ready.", rest: "" },
              ],
              accent: "#FFD400",
              side: "right",
            },
            {
              eyebrow: "Step 03",
              title: "Your Products Are Already Waiting For You.",
              emoji: ["🪄", "📦", "🎯"],
              lines: [
                { strong: "Here's where eGeez gets remarkable.", rest: " Our licensing team, fan community, and proprietary eSmart AI — powered by our Licensed Consumer Products Intelligence system — have likely already indexed your products from public sources before you even signed up." },
                { strong: "Go to Product List → hit Claim,", rest: " copy and enter the unique code sent to your email, and watch your entire product catalogue load instantly." },
                { strong: "Always review before going live.", rest: " Set your product prices in line with your eGeez merchant terms — then activate and start selling." },
                { strong: "No product listed yet? No problem.", rest: " Head to your eLicensee dashboard → Product List → Add Product." },
              ],
              accent: "#0041E6",
              side: "left",
              addMethods: true,
            },
          ].map((step, i) => (
            <motion.div
              key={i}
              initial={{ opacity: 0, y: 40 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-120px" }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className={`relative mb-24 md:mb-32 grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-16 items-center ${
                step.side === "right" ? "lg:[&>*:first-child]:order-2" : ""
              }`}
              data-testid={`step-${i + 1}`}
            >
              {/* LEFT — Visual mockup */}
              <div className={`flex justify-center ${step.side === "right" ? "lg:justify-start" : "lg:justify-end"}`}>
                <StepVisual index={i} accent={step.accent} step1Img={A.step1Dashboard} step3Img={A.step3Products} />
              </div>

              {/* RIGHT — Content with inline numbered marker */}
              <div className={`${step.side === "right" ? "lg:pr-12 lg:text-right" : "lg:pl-12"}`}>
                {/* Inline marker + eyebrow */}
                <div className={`flex items-center gap-3 mb-4 ${step.side === "right" ? "lg:justify-end" : ""}`}>
                  <motion.div
                    animate={{ y: [0, -3, 0] }}
                    transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: i * 0.3 }}
                    className="relative w-12 h-12 rounded-full flex items-center justify-center shadow-[0_12px_24px_-8px_rgba(0,65,230,0.35)] ring-4 ring-white flex-shrink-0"
                    style={{ background: step.accent }}
                  >
                    <span
                      className="font-display italic font-black"
                      style={{
                        fontSize: "20px",
                        color: step.accent === "#FFD400" ? "#0041E6" : "#FFFFFF",
                      }}
                    >
                      0{i + 1}
                    </span>
                    <motion.div
                      className="absolute inset-0 rounded-full border-2"
                      style={{ borderColor: step.accent }}
                      animate={{ scale: [1, 1.4, 1.4], opacity: [0.6, 0, 0] }}
                      transition={{ duration: 2.4, repeat: Infinity, ease: "easeOut", delay: i * 0.3 }}
                    />
                  </motion.div>
                  <div
                    className="text-[10px] font-black tracking-[0.32em] uppercase"
                    style={{ color: step.accent === "#FFD400" ? "#D4A400" : step.accent }}
                  >
                    {step.eyebrow}
                  </div>
                </div>
                <h3
                  className="font-display text-black leading-[0.98]"
                  style={{ fontSize: "clamp(28px, 3.5vw, 44px)" }}
                >
                  {step.title}
                </h3>
                {/* Emoji row */}
                <div className={`mt-5 flex gap-3 text-[28px] md:text-[32px] ${step.side === "right" ? "lg:justify-end" : ""}`}>
                  {step.emoji.map((e, j) => (
                    <motion.span
                      key={j}
                      aria-hidden
                      animate={{ y: [0, -4, 0], rotate: [-4, 4, -4] }}
                      transition={{ duration: 3 + j * 0.3, repeat: Infinity, ease: "easeInOut", delay: j * 0.2 }}
                      className="inline-block select-none"
                    >
                      {e}
                    </motion.span>
                  ))}
                </div>
                <div className="mt-5 space-y-3 text-[15px] md:text-[16px] text-black/75 leading-relaxed">
                  {step.lines.map((ln, j) => (
                    <p key={j}>
                      <strong className="text-black">{ln.strong}</strong>{ln.rest}
                    </p>
                  ))}
                </div>

                {step.addMethods && (
                  <div className={`mt-6 grid grid-cols-2 gap-3 max-w-md ${step.side === "right" ? "lg:ml-auto" : ""}`}>
                    {[
                      { icon: "⚡", label: "Bulk Import" },
                      { icon: "🔗", label: "API Integration", soon: true },
                      { icon: "👑", label: "VIP Support" },
                      { icon: "✍️", label: "Manual Listing" },
                    ].map((m, k) => (
                      <motion.div
                        key={k}
                        whileHover={{ y: -3, scale: 1.02 }}
                        className="relative flex items-center gap-2 bg-white border border-black/10 rounded-xl px-3 py-2.5 shadow-sm"
                      >
                        <span className="text-[18px]">{m.icon}</span>
                        <span className="text-[13px] font-bold text-black">{m.label}</span>
                        {m.soon && (
                          <span className="absolute -top-2 -right-2 bg-[#FFD400] text-black text-[8px] font-black tracking-wider uppercase px-1.5 py-0.5 rounded-full">
                            Soon
                          </span>
                        )}
                      </motion.div>
                    ))}
                  </div>
                )}

                {i === 2 && (
                  <div className={`mt-6 ${step.side === "right" ? "lg:text-right" : ""}`}>
                    <p className={`font-display italic text-[18px] md:text-[22px] text-[#0041E6] leading-snug max-w-xl flex items-start gap-2 ${step.side === "right" ? "lg:ml-auto lg:flex-row-reverse" : ""}`}>
                      <span className="inline-flex items-center justify-center flex-shrink-0 w-7 h-7 rounded-full bg-[#22C55E] mt-1 shadow-md">
                        <Check size={16} className="text-white" strokeWidth={3} />
                      </span>
                      <span>Done. Activated. Selling. Instantly geolocated — like Tinder profiles appearing in your area, but for licensed products in verified territories.</span>
                    </p>
                    <p className="mt-2 text-[13px] font-bold tracking-wider uppercase text-black/50">
                      Compliance built in · Zero guesswork
                    </p>
                  </div>
                )}
              </div>
            </motion.div>
          ))}

          {/* CTA after 3 steps */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mt-16"
          >
            <a
              href={T.signUp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="three-steps-sign-up-btn"
              className="group inline-flex items-center gap-3 bg-[#0041E6] hover:bg-[#0033B8] text-white font-bold text-[18px] rounded-full pl-8 pr-2 h-[62px] transition shadow-[0_18px_40px_-14px_rgba(0,65,230,0.55)]"
            >
              Start Step 01
              <span className="w-[46px] h-[46px] rounded-full bg-white text-[#0041E6] flex items-center justify-center transition-transform group-hover:rotate-45">
                <ArrowUpRight size={20} />
              </span>
            </a>
            <a
              href="https://help.egeez.com/"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="three-steps-resources-btn"
              className="ml-3 group inline-flex items-center gap-2 border-2 border-black/20 hover:border-[#0041E6] text-black hover:text-[#0041E6] font-bold text-[16px] rounded-full px-6 h-[62px] transition"
            >
              Resources
              <ArrowUpRight size={18} />
            </a>
            <div className="mt-4 inline-flex items-center gap-2 text-[11px] tracking-[0.25em] uppercase text-black/40 font-bold">
              via
              <img src={A.eLicenseeLogo} alt="eLicensee" className="h-5 w-auto" draggable={false} />
            </div>
          </motion.div>
        </div>

        {/* ===== SPECIAL PERKS — GOLDEN VERIFIED CARD ===== */}
        <div className="mx-auto max-w-[1200px] px-5 md:px-12 mt-32 relative" data-testid="special-perks-card">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.9, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[40px] p-[2px] overflow-hidden"
            style={{
              background:
                "linear-gradient(135deg, #F5D16A 0%, #FFD700 30%, #FFF3B0 50%, #C9972B 75%, #FFD700 100%)",
            }}
          >
            {/* Animated gold glow ring */}
            <motion.div
              animate={{ rotate: [0, 360] }}
              transition={{ duration: 20, repeat: Infinity, ease: "linear" }}
              className="absolute -inset-20 opacity-40 pointer-events-none"
              aria-hidden
            >
              <div
                className="absolute inset-0"
                style={{
                  background:
                    "conic-gradient(from 0deg, transparent 0%, #FFD700 20%, transparent 40%, #F5D16A 60%, transparent 80%, #FFD700 100%)",
                  filter: "blur(40px)",
                }}
              />
            </motion.div>

            <div
              className="relative rounded-[38px] p-10 md:p-16 overflow-hidden"
              style={{
                background:
                  "radial-gradient(ellipse at top left, #1a1008 0%, #0a0a0a 70%, #000000 100%)",
              }}
            >
              {/* Floating sparkles */}
              {[{ x: "10%", y: "20%", d: 0 }, { x: "88%", y: "15%", d: 0.5 }, { x: "92%", y: "78%", d: 1 }, { x: "12%", y: "82%", d: 1.5 }].map((sp, i) => (
                <motion.span
                  key={i}
                  aria-hidden
                  style={{ left: sp.x, top: sp.y }}
                  animate={{ opacity: [0.3, 1, 0.3], scale: [0.8, 1.2, 0.8], rotate: [0, 180, 360] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut", delay: sp.d }}
                  className="absolute text-[#FFD700] pointer-events-none"
                >
                  <Sparkles size={20} fill="#FFD700" />
                </motion.span>
              ))}

              <div className="relative z-10 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                <div className="lg:col-span-7">
                  <div className="inline-flex items-center gap-2 mb-5 bg-gradient-to-r from-[#FFD700] to-[#F5D16A] text-black text-[10px] md:text-[11px] font-black tracking-[0.3em] uppercase px-3.5 py-1.5 rounded-full shadow-[0_10px_30px_-5px_rgba(255,215,0,0.5)]">
                    ✨ Special Perks · Golden Verified
                  </div>
                  <h3
                    className="font-display leading-[0.95]"
                    style={{
                      fontSize: "clamp(32px, 4.6vw, 62px)",
                      background: "linear-gradient(135deg, #FFF3B0 0%, #FFD700 40%, #F5D16A 70%, #C9972B 100%)",
                      WebkitBackgroundClip: "text",
                      WebkitTextFillColor: "transparent",
                      backgroundClip: "text",
                    }}
                  >
                    Upload your licensing agreement.<br />
                    <span className="italic">Unlock everything.</span>
                  </h3>
                  <p className="mt-5 text-[15px] md:text-[16px] leading-relaxed text-white/80 max-w-xl">
                    Drop your licensing agreement under <strong className="text-[#FFD700]">My Licensor</strong> and it's done.
                  </p>
                  <p className="mt-3 text-[15px] md:text-[16px] leading-relaxed text-white/80 max-w-xl">
                    You're not just verifying — you're powering the <strong className="text-[#FFD700]">IPx Cloud</strong>, making eGeez's intelligence sharper for every licensee on the platform.
                  </p>

                  <div className="mt-8 space-y-3">
                    {[
                      { i: "🥇", t: "Golden Verification Icon", b: "Visible to fans in-app — signals trust, drives purchase confidence." },
                      { i: "🏆", t: "Top Featured Listings", b: "Maximum visibility across all your licensed territories — competitive edge." },
                      { i: "📣", t: "Priority Digital Advertising", b: "eGeez promotes YOUR products first." },
                      { i: "📊", t: "Early Access Discount", b: "On eInsights, royalty reconciliation and reporting subscriptions when launched." },
                    ].map((p, j) => (
                      <motion.div
                        key={j}
                        initial={{ opacity: 0, x: -12 }}
                        whileInView={{ opacity: 1, x: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.5, delay: j * 0.08 }}
                        className="flex items-start gap-3 rounded-2xl bg-white/5 backdrop-blur-sm border border-[#FFD700]/20 px-4 py-3 hover:bg-[#FFD700]/10 hover:border-[#FFD700]/40 transition-colors"
                      >
                        <span className="text-[22px] mt-0.5">{p.i}</span>
                        <div>
                          <div className="text-[14px] md:text-[15px] font-bold text-[#FFD700]">{p.t}</div>
                          <div className="text-[13px] text-white/70 leading-snug mt-0.5">{p.b}</div>
                        </div>
                      </motion.div>
                    ))}
                  </div>

                  <div className="mt-8">
                    <a
                      href={T.signUp}
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="perks-sign-up-btn"
                      className="group inline-flex items-center gap-3 font-bold text-[16px] rounded-full pl-6 pr-1.5 h-[54px] transition shadow-[0_14px_35px_-8px_rgba(255,215,0,0.6)]"
                      style={{
                        background: "linear-gradient(135deg, #FFD700 0%, #F5D16A 50%, #C9972B 100%)",
                        color: "#000",
                      }}
                    >
                      Go Golden
                      <span className="w-[40px] h-[40px] rounded-full bg-black text-[#FFD700] flex items-center justify-center transition-transform group-hover:rotate-45">
                        <ArrowUpRight size={18} />
                      </span>
                    </a>
                  </div>
                </div>

                {/* Right side — Verified seal (floating, glowing) */}
                <div className="lg:col-span-5 flex justify-center relative">
                  <motion.div
                    animate={{ y: [0, -12, 0], rotate: [-2, 2, -2] }}
                    transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                    className="relative w-[220px] md:w-[300px]"
                  >
                    {/* Intense gold glow */}
                    <motion.div
                      animate={{ opacity: [0.4, 0.9, 0.4], scale: [1, 1.1, 1] }}
                      transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut" }}
                      className="absolute -inset-10 rounded-full pointer-events-none"
                      style={{ background: "radial-gradient(circle, rgba(255,215,0,0.5) 0%, transparent 70%)" }}
                      aria-hidden
                    />
                    <img
                      src={A.verifiedSeal}
                      alt="eGeez Verified — Golden Seal"
                      className="relative w-full h-auto drop-shadow-[0_30px_60px_rgba(255,215,0,0.45)]"
                      draggable={false}
                    />
                    {/* Ribbon banner below */}
                    <motion.div
                      initial={{ opacity: 0, y: 10 }}
                      whileInView={{ opacity: 1, y: 0 }}
                      viewport={{ once: true }}
                      transition={{ duration: 0.6, delay: 0.4 }}
                      className="absolute -bottom-4 left-1/2 -translate-x-1/2 bg-black text-[#FFD700] text-[10px] font-black tracking-[0.3em] uppercase px-4 py-1.5 rounded-full border border-[#FFD700]/40 shadow-xl whitespace-nowrap"
                    >
                      ★ Golden Tier ★
                    </motion.div>
                  </motion.div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* 11.5. eSHIP PROGRAM */}
      <section className="relative py-24 md:py-32 bg-white overflow-hidden" data-testid="eship-section">
        <div className="absolute inset-0 -z-0 pointer-events-none" aria-hidden>
          <div className="absolute top-1/3 -left-40 w-[500px] h-[500px] rounded-full bg-[#0041E6]/6 blur-[120px]" />
          <div className="absolute bottom-1/4 -right-40 w-[500px] h-[500px] rounded-full bg-[#FFD400]/8 blur-[120px]" />
        </div>

        <div className="mx-auto max-w-[1200px] px-5 md:px-12 relative">
          {/* eShip logo prominently at top */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7 }}
            className="text-center mb-14 md:mb-20"
          >
            <div className="inline-flex items-center gap-2 text-[11px] font-black tracking-[0.32em] uppercase text-[#0041E6] mb-6">
              <span className="w-6 h-px bg-[#0041E6]" />
              Program
              <span className="w-6 h-px bg-[#0041E6]" />
            </div>
            <motion.img
              animate={{ y: [0, -6, 0] }}
              transition={{ duration: 5, repeat: Infinity, ease: "easeInOut" }}
              src={A.eShipLogo}
              alt="eShip"
              className="mx-auto h-[140px] sm:h-[180px] md:h-[220px] lg:h-[260px] w-auto"
              draggable={false}
            />
            <p className="mt-7 font-display text-black leading-[0.95]" style={{ fontSize: "clamp(28px, 4vw, 48px)" }}>
              You&apos;re licensed. <span className="italic text-[#0041E6]">You belong here.</span>
            </p>
            <p className="mt-3 text-[16px] md:text-[18px] text-black/60 italic">
              Even if you don&apos;t dropship — yet.
            </p>
          </motion.div>

          {/* MAIN — 2 column layout */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            {/* LEFT — copy */}
            <div className="space-y-8">
              <div>
                <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-[0.28em] uppercase text-black/45 mb-3">
                  📦 The Reality
                </div>
                <p className="text-[15px] md:text-[16px] text-black/80 leading-relaxed">
                  eGeez runs on dropshipping — fans discover, click, and your product ships directly from you to them. But not every licensee ships that way. And we&apos;re not here to leave you out.
                </p>
                <p className="mt-3 font-display italic text-[#0041E6] text-[20px] md:text-[24px] leading-snug">
                  That&apos;s exactly why we built eShip.
                </p>
              </div>

              <div>
                <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-[0.28em] uppercase text-black/45 mb-3">
                  ⚙️ How eShip Works
                </div>
                <p className="text-[15px] md:text-[16px] text-black/80 leading-relaxed">
                  No dropshipping capability? <strong>No problem.</strong> eGeez partners with vetted dropshipping specialists who source on-demand quantities and handle fulfillment on your behalf — so your licensed products still reach fans, compliantly, in your authorized territories.
                </p>
                <p className="mt-3 text-[15px] md:text-[16px] text-black/85">
                  <strong>You focus on what you do best. eShip handles the rest.</strong>
                </p>
              </div>

              <div className="rounded-2xl bg-[#FFF8E1] border border-[#FFD400]/40 p-5">
                <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-[0.28em] uppercase text-[#A07800] mb-2">
                  ⚠️ One Important Thing
                </div>
                <p className="text-[14px] md:text-[15px] text-black/80 leading-relaxed">
                  eShip is for licensees who genuinely don&apos;t have direct shipping capability. If your website already has a shopping cart — <strong>you ship.</strong> That means standard eGeez onboarding, not eShip.
                </p>
                <p className="mt-2 text-[13px] text-black/60 italic">
                  We check. We&apos;re thorough. But we&apos;re always fair — let&apos;s talk.
                </p>
              </div>
            </div>

            {/* RIGHT — floating phone mockup */}
            <motion.div
              initial={{ opacity: 0, scale: 0.94, y: 20 }}
              whileInView={{ opacity: 1, scale: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
              className="flex justify-center"
            >
              <motion.div
                whileHover={{ y: -8, rotate: 0 }}
                animate={{ y: [0, -12, 0], rotate: [-1.5, 1.5, -1.5] }}
                transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
                className="relative w-full max-w-[420px]"
              >
                <div className="absolute -inset-10 bg-[#0041E6]/15 blur-[80px] rounded-full pointer-events-none" aria-hidden />
                {/* Floating delivery emojis */}
                <motion.span
                  aria-hidden
                  animate={{ y: [0, -12, 0], rotate: [-8, 8, -8] }}
                  transition={{ duration: 4, repeat: Infinity, ease: "easeInOut" }}
                  className="absolute -top-4 -right-2 z-20 text-3xl select-none pointer-events-none"
                >
                  🚚
                </motion.span>
                <motion.span
                  aria-hidden
                  animate={{ y: [0, -8, 0] }}
                  transition={{ duration: 3.5, repeat: Infinity, ease: "easeInOut", delay: 0.6 }}
                  className="absolute top-10 -left-3 z-20 text-2xl select-none pointer-events-none"
                >
                  📦
                </motion.span>
                <motion.span
                  aria-hidden
                  animate={{ y: [0, -6, 0], rotate: [0, 12, 0] }}
                  transition={{ duration: 3.8, repeat: Infinity, ease: "easeInOut", delay: 1.2 }}
                  className="absolute -bottom-4 right-6 z-20 text-2xl select-none pointer-events-none"
                >
                  ✨
                </motion.span>
                <img
                  src={A.eShipAppScreen}
                  alt="eGeez Central Licensing Marketplace — eShip"
                  className="relative w-full h-auto drop-shadow-[0_30px_60px_rgba(0,65,230,0.25)]"
                  draggable={false}
                />
              </motion.div>
            </motion.div>
          </div>

          {/* Animated divider before bottom card */}
          <div className="relative my-16 md:my-20 flex items-center gap-4 max-w-3xl mx-auto" aria-hidden>
            <div className="flex-1 h-[2px] bg-gradient-to-r from-transparent via-[#0041E6]/30 to-transparent rounded-full" />
            <motion.span
              animate={{ y: [0, -4, 0] }}
              transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
              className="text-2xl"
            >
              ✨
            </motion.span>
            <div className="flex-1 h-[2px] bg-gradient-to-r from-transparent via-[#0041E6]/30 to-transparent rounded-full" />
          </div>

          {/* Bottom card — Not Ready to Ship Yet */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-80px" }}
            transition={{ duration: 0.8, ease: [0.16, 1, 0.3, 1] }}
            className="relative rounded-[32px] p-10 md:p-14 overflow-hidden"
            style={{ background: "linear-gradient(135deg, #F0F4FF 0%, #E5EBFF 100%)" }}
          >
            <div className="absolute -top-16 -right-16 w-[300px] h-[300px] rounded-full bg-[#0041E6]/8 blur-3xl pointer-events-none" />
            <div className="relative z-10 grid grid-cols-1 md:grid-cols-12 gap-8 md:gap-10 items-center">
              <div className="md:col-span-7">
                <div className="inline-flex items-center gap-2 text-[10px] font-black tracking-[0.28em] uppercase text-[#0041E6] mb-3">
                  🌟 Still Onboard
                </div>
                <h3
                  className="font-display text-black leading-[0.95]"
                  style={{ fontSize: "clamp(28px, 3.6vw, 48px)" }}
                >
                  Not Ready To Ship Yet?<br />
                  <span className="italic text-[#0041E6]">Still Onboard.</span>
                </h3>
                <p className="mt-5 text-[15px] md:text-[16px] text-black/80 leading-relaxed">
                  Every licensee deserves to shine on eGeez — shipping capability or not. Sign up now and your brand appears in the <strong>eGeez Central Licensing Marketplace</strong> as a verified <strong>promotional directory listing</strong>. Fans discover you. Licensors see you. Your products are visible.
                </p>
                <p className="mt-3 text-[15px] md:text-[16px] text-black/85">
                  When you&apos;re ready to sell — <strong>flip the switch.</strong> Your products go live instantly.
                </p>
                <p className="mt-5 font-display italic text-[18px] md:text-[22px] text-[#0041E6] leading-snug">
                  The licensing world is big enough for every legitimate licensee.<br />
                  <span className="text-black">eGeez is built to prove it.</span>
                </p>
              </div>
              <div className="md:col-span-5 flex justify-center">
                <motion.div
                  animate={{ y: [0, -6, 0] }}
                  transition={{ duration: 4.5, repeat: Infinity, ease: "easeInOut" }}
                  className="relative w-full max-w-[260px] rounded-[24px] bg-white p-5 shadow-[0_30px_60px_-15px_rgba(0,65,230,0.25)] ring-1 ring-[#0041E6]/15"
                >
                  <div className="text-[9px] font-black tracking-[0.28em] uppercase text-black/45 mb-3">
                    Directory Listing
                  </div>
                  <div className="flex items-center gap-3 mb-3">
                    <div className="w-12 h-12 rounded-xl bg-[#0041E6] flex items-center justify-center text-white text-xl">🏷️</div>
                    <div className="flex-1 min-w-0">
                      <div className="text-[13px] font-bold text-black truncate">Your Brand · Verified</div>
                      <div className="text-[10px] text-[#0041E6] font-bold flex items-center gap-1">
                        <Check size={10} strokeWidth={3} /> eGeez Verified
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center justify-between gap-2 rounded-xl bg-[#F0FDF4] border border-[#86EFAC] px-3 py-2.5">
                    <span className="text-[11px] font-bold text-[#15803D]">Sell when ready</span>
                    <motion.div
                      whileHover={{ x: 2 }}
                      className="relative w-10 h-5 rounded-full bg-black/10 cursor-pointer"
                    >
                      <motion.span
                        animate={{ x: [0, 20, 0] }}
                        transition={{ duration: 3, repeat: Infinity, ease: "easeInOut" }}
                        className="absolute top-0.5 left-0.5 w-4 h-4 rounded-full bg-[#22C55E] shadow-md"
                      />
                    </motion.div>
                  </div>
                  <div className="mt-3 text-[10px] text-black/50 text-center font-bold uppercase tracking-wider">
                    Flip the switch · Go live
                  </div>
                </motion.div>
              </div>
            </div>
          </motion.div>

          {/* CTAs */}
          <div className="mt-12 flex flex-wrap items-center justify-center gap-4">
            <a
              href={T.signUp}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="eship-sign-up-btn"
              className="group inline-flex items-center gap-3 bg-[#0041E6] hover:bg-[#0033B8] text-white font-bold text-[17px] rounded-full pl-7 pr-2 h-[58px] transition shadow-[0_18px_40px_-14px_rgba(0,65,230,0.55)]"
            >
              Sign Up Now
              <span className="w-[42px] h-[42px] rounded-full bg-white text-[#0041E6] flex items-center justify-center transition-transform group-hover:rotate-45">
                <ArrowUpRight size={18} />
              </span>
            </a>
            <a
              href={withUTM("https://uu6leb0wjug.typeform.com/to/j7k9u4Ij", "for-licensees")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="eship-onboard-talk-btn"
              className="group inline-flex items-center gap-2 border-2 border-[#0041E6] text-[#0041E6] hover:bg-[#0041E6] hover:text-white font-bold text-[17px] rounded-full px-7 h-[58px] transition"
            >
              Let&apos;s Onboard &amp; Talk
              <ArrowUpRight size={18} />
            </a>
          </div>
        </div>
      </section>

      {/* 11.9 LinkedIn Follow */}
      <section className="relative py-16 bg-white" data-testid="linkedin-section">
        <div className="mx-auto max-w-[900px] px-5 md:px-12 text-center">
          <div className="text-[10px] font-black tracking-[0.32em] uppercase text-black/40 mb-4">
            Stay in the loop
          </div>
          <p className="font-display text-black leading-tight mb-6" style={{ fontSize: "clamp(22px, 3vw, 36px)" }}>
            Follow eGeez on LinkedIn for licensing news &amp; product updates.
          </p>
          <a
            href="https://www.linkedin.com/company/egeez/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="linkedin-follow-btn"
            className="group inline-flex items-center gap-3 bg-[#0A66C2] hover:bg-[#0855A0] text-white font-bold text-[16px] rounded-full pl-7 pr-2 h-[54px] transition shadow-[0_14px_30px_-12px_rgba(10,102,194,0.55)]"
          >
            <span aria-hidden className="inline-flex items-center justify-center w-5 h-5 rounded-sm bg-white text-[#0A66C2] font-black text-[11px]">in</span>
            Follow eGeez on LinkedIn
            <span className="w-[40px] h-[40px] rounded-full bg-white text-[#0A66C2] flex items-center justify-center transition-transform group-hover:rotate-45">
              <ArrowUpRight size={18} />
            </span>
          </a>
        </div>
      </section>

      {/* 12. FINAL CTA */}
      <section className="relative py-24 md:py-32 bg-[#FAFAFA]">
        <div className="mx-auto max-w-[1200px] px-5 md:px-12">
          <motion.div
            {...fadeUp}
            className="relative rounded-[40px] overflow-hidden p-12 md:p-20 text-white text-center"
            style={{
              backgroundImage:
                "url(https://customer-assets.emergentagent.com/job_toon-threads-3d/artifacts/ktrq6q6g_Screenshot%202026-05-02%20124238.png)",
              backgroundSize: "cover",
              backgroundPosition: "center",
              backgroundColor: "#0041E6",
            }}
            data-testid="final-cta-licensees"
          >
            {/* Layered overlays for readability */}
            <div className="absolute inset-0 bg-black/55" aria-hidden />
            <div
              className="absolute inset-0"
              style={{
                background:
                  "linear-gradient(135deg, rgba(0, 65, 230, 0.55) 0%, rgba(0, 33, 110, 0.7) 100%)",
              }}
              aria-hidden
            />
            <div className="relative z-10">
              <Eyebrow color="#FFD400">Ready to onboard</Eyebrow>
              <h2 className="mt-5 font-display text-white text-[44px] md:text-[80px] leading-[0.95] drop-shadow-[0_4px_20px_rgba(0,0,0,0.45)]">
                Sell to fans<br />who actually care.
              </h2>
              <p className="mt-6 text-[18px] md:text-[20px] text-white/95 max-w-xl mx-auto leading-snug drop-shadow-[0_2px_8px_rgba(0,0,0,0.4)]">
                Get verified, upload your catalog, and start selling. We handle the marketplace — you keep more of every sale.
              </p>
              <div className="mt-6 inline-flex items-center gap-2 bg-white/15 backdrop-blur-md rounded-full px-3.5 py-1.5 border border-white/20">
                <span className="text-[10px] font-bold tracking-[0.28em] uppercase text-white/85">Live on</span>
                <img src={A.eLicenseeLogo} alt="eLicensee" className="h-5 w-auto brightness-0 invert opacity-95" draggable={false} />
              </div>
              <div className="mt-10 flex flex-wrap items-center justify-center gap-4">
                <a href={T.signUp} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 bg-white text-[#0041E6] font-bold text-[18px] rounded-full pl-8 pr-2 h-[60px] hover:bg-[#FFD400] transition shadow-2xl" data-testid="final-sign-up-btn">
                  Sign-Up
                  <span className="w-[44px] h-[44px] rounded-full bg-[#0041E6] text-white flex items-center justify-center">
                    <ArrowUpRight size={20} />
                  </span>
                </a>
                <a href={T.onboardNow} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 border-2 border-white text-white font-bold text-[18px] rounded-full px-8 h-[60px] hover:bg-white hover:text-[#0041E6] transition">
                  Onboard Now
                </a>
              </div>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
